import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { 
  Shield, 
  Zap, 
  Trophy, 
  ChevronRight, 
  ChevronDown,
  Check, 
  X, 
  Briefcase, 
  GraduationCap, 
  Wrench, 
  HelpCircle,
  Star,
  Activity,
  Quote,
  CreditCard,
  Wallet,
  BadgeCheck,
  Globe
} from 'lucide-react';
import "@/App.css";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { toast, Toaster } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Seal URLs
const SEALS = {
  focusMoney: 'https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/h2m0hol1_Download%20%2857%29.png',
  ascore: 'https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/bejw5xdw_Download%20%2856%29.png',
  asscompact2025: 'https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/347gm1ec_Download%20%2823%29.jpg',
  asscompact2020: 'https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/i3byrpzf_Download.jpg',
};

// Injury data - einheitliche Auszahlung pro Paket (nicht verletzungsabhängig)
const INJURIES = [
  { id: 0, name: 'Außenbandriss', zone: 'ankle', description: 'Außenbandverletzung am Knöchel' },
  { id: 1, name: 'Muskelfaserriss', zone: 'muscle', description: 'Riss der Muskelfasern' },
  { id: 2, name: 'Wadenbeinbruch', zone: 'bone', description: 'Fraktur Wadenbein' },
  { id: 3, name: 'Kreuzbandriss', zone: 'knee', description: 'Kreuzband komplett gerissen' },
  { id: 4, name: 'Achillessehnenriss', zone: 'ankle', description: 'Kompletter Abriss der Achillessehne' },
  { id: 5, name: 'Zahnschaden', zone: 'teeth', description: 'Natürliche Zähne unbegrenzt, Zahnersatz max. 2.500€' },
];

// Einheitliche Auszahlung pro Paket
const PACKAGE_PAYOUTS = {
  joker: 1000,
  stammplatz: 3000,
  captain: 5000,
};

// Package data
const PACKAGES = [
  {
    id: 'joker',
    name: 'Basic',
    subtitle: '',
    price: 4.99,
    payout: 1000,
    features: [
      'Schmerzensgeld: 1.000€',
      'Brüche & Risse abgedeckt',
      'Zahnschutz inklusive',
      '24/7 Hotline',
    ],
    highlighted: false,
  },
  {
    id: 'stammplatz',
    name: 'Pro',
    subtitle: '',
    price: 9.99,
    payout: 3000,
    features: [
      'Schmerzensgeld: 3.000€',
      'Alle Brüche, Risse & Bänder',
      'Natürliche Zähne: unbegrenzt',
      'Zahnersatz: max. 2.500€',
      'Express-Auszahlung in 48h',
      'Kein Papierkram – digital & schnell',
    ],
    highlighted: true,
  },
  {
    id: 'captain',
    name: 'Elite',
    subtitle: '',
    price: 14.99,
    payout: 5000,
    features: [
      'Schmerzensgeld: 5.000€',
      'Brüche & Risse abgedeckt',
      'Natürliche Zähne: unbegrenzt',
      'Zahnersatz: max. 2.500€',
      '+ 25€ Krankenhaustagegeld',
      'Premium Support',
    ],
    highlighted: false,
  },
];

// Testimonials
const TESTIMONIALS = [
  {
    id: 1,
    name: 'Sarah M.',
    team: 'FC Grünheide, Kreisliga A',
    quote: 'Sonntag umgeknickt, Montag Diagnose, Mittwoch war die Kohle auf dem Konto. KICKBACK ist der MVP.',
    image: 'https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/y74sgd06_unnamed%20-%202026-02-21T105635.752.jpg',
    injury: 'Außenbandriss',
    payout: '1.000€'
  },
  {
    id: 2,
    name: 'Marco S.',
    team: 'TSV 1860 Berg, Bezirksliga',
    quote: 'Kreuzband durch, aber wenigstens konnte ich mir den Reha-Urlaub leisten. Danke KICKBACK!',
    image: 'https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/r7kfq0ax_unnamed%20-%202026-02-21T110148.329.jpg',
    injury: 'Kreuzbandriss',
    payout: '5.000€'
  },
  {
    id: 3,
    name: 'Tim W.',
    team: 'SG Westend, Landesliga',
    quote: 'Mein Trainer hat mich angemeldet. Beste Entscheidung ever. Nach dem Wadenbeinbruch war ich abgesichert.',
    image: 'https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/iyzb6lrm_unnamed%20-%202026-02-21T110219.810.jpg',
    injury: 'Wadenbeinbruch',
    payout: '3.000€'
  },
];

// FAQ Data
const FAQ_DATA = [
  {
    q: 'Zahlt ihr auch bei Fouls?',
    a: 'Ja, egal ob Eigenverschulden oder Sense vom Gegner. Solange es auf dem Platz passiert, bist du abgesichert.'
  },
  {
    q: 'Wann kommt das Geld?',
    a: 'Sobald der ärztliche Befund hochgeladen ist, prüfen wir digital. In der Regel innerhalb von 48 Stunden auf deinem Konto. Kein langes Warten.'
  },
  {
    q: 'Wer steckt dahinter?',
    a: 'Hinter KICKBACK stehen drei starke Versicherungsgesellschaften: Allianz, Gothaer und Württembergische. Gebündelt in der Konzept & Marketing GmbH aus Hannover – mit über 20 Jahren Erfahrung in der Versicherungsbranche.'
  },
  {
    q: 'Gilt das auch für Training?',
    a: 'Ja! Training, Freundschaftsspiele, Turniere – alles abgedeckt. Auch der Bolzplatz am Wochenende.'
  },
  {
    q: 'Kann ich jederzeit kündigen?',
    a: 'Monatlich kündbar nach Ablauf der Mindestlaufzeit von 12 Monaten. Keine versteckten Kosten. Flexibel wie ein Mittelfeldspieler.'
  },
];

// IBAN Validation
const validateIBAN = (iban) => {
  const cleanIban = iban.replace(/\s/g, '').toUpperCase();
  if (cleanIban.length < 15 || cleanIban.length > 34) return false;
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(cleanIban)) return false;
  const rearranged = cleanIban.slice(4) + cleanIban.slice(0, 4);
  const numericString = rearranged.replace(/[A-Z]/g, (char) => (char.charCodeAt(0) - 55).toString());
  let remainder = numericString;
  while (remainder.length > 2) {
    const block = remainder.slice(0, 9);
    remainder = (parseInt(block, 10) % 97).toString() + remainder.slice(9);
  }
  return parseInt(remainder, 10) % 97 === 1;
};

const formatIBAN = (value) => {
  const clean = value.replace(/\s/g, '').toUpperCase();
  const groups = clean.match(/.{1,4}/g);
  return groups ? groups.join(' ') : '';
};

// Counter Animation Hook
const useCountUp = (target, duration = 500) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    let startTime;
    let animationFrame;
    const startValue = count;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(startValue + (target - startValue) * easeOut));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [target, duration]);
  
  return count;
};

// Football SVG Icon Component
const FootballIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" className={className}>
    <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" strokeWidth="3"/>
    <path d="M50 2 L50 25 M50 75 L50 98 M2 50 L25 50 M75 50 L98 50" stroke="currentColor" strokeWidth="1.5" opacity="0.4"/>
    <polygon points="50,18 62,35 58,55 42,55 38,35" fill="none" stroke="currentColor" strokeWidth="2.5"/>
    <line x1="50" y1="18" x2="50" y2="2" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/>
    <line x1="62" y1="35" x2="80" y2="25" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/>
    <line x1="58" y1="55" x2="75" y2="70" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/>
    <line x1="42" y1="55" x2="25" y2="70" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/>
    <line x1="38" y1="35" x2="20" y2="25" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/>
  </svg>
);

// Animated Section Header
const SectionHeader = ({ tag, title, highlight, subtitle }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
    className="text-center mb-16"
  >
    {tag && (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-lime-200/10 border border-lime-200/20 mb-5"
      >
        <div className="w-1.5 h-1.5 rounded-full bg-lime-200 animate-pulse" />
        <span className="text-xs text-lime-200 uppercase tracking-widest font-medium">{tag}</span>
      </motion.div>
    )}
    <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-4 italic">
      {title} <span className="text-lime-200 text-glow-lime">{highlight}</span>
    </h2>
    {subtitle && (
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
        className="text-slate-400 max-w-xl mx-auto"
      >{subtitle}</motion.p>
    )}
    <motion.div
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true }}
      transition={{ delay: 0.4, duration: 0.6 }}
      className="mt-6 mx-auto w-20 h-0.5 bg-gradient-to-r from-transparent via-lime-200/60 to-transparent"
    />
  </motion.div>
);

// Scroll Progress Bar
const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-[2px] bg-lime-200 z-50 origin-left"
      style={{ scaleX: scrollYProgress }}
    />
  );
};

// Floating Footballs Background
const FloatingFootballs = () => {
  const balls = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 8,
    duration: 15 + Math.random() * 20,
    size: 16 + Math.random() * 24,
    opacity: 0.04 + Math.random() * 0.08,
    rotation: Math.random() * 360,
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {balls.map((ball) => (
        <motion.div
          key={ball.id}
          className="absolute text-lime-200"
          style={{ left: `${ball.x}%`, opacity: ball.opacity }}
          initial={{ bottom: -60, rotate: 0 }}
          animate={{
            bottom: ['0%', '110%'],
            rotate: [0, ball.rotation > 180 ? 360 : -360],
            x: [0, Math.sin(ball.id * 0.7) * 80, 0],
          }}
          transition={{
            duration: ball.duration,
            repeat: Infinity,
            delay: ball.delay,
            ease: 'linear',
          }}
        >
          <FootballIcon size={ball.size} />
        </motion.div>
      ))}
    </div>
  );
};

// Hero Section Component
const HeroSection = ({ onCtaClick }) => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 600], [0, 200]);
  const contentOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Hero Football Image */}
      <motion.div
        className="absolute inset-0"
        style={{ y: bgY }}
      >
        <img
          src="https://static.prod-images.emergentagent.com/jobs/2b82e33f-bdf0-4b9d-bca9-c432e89960d3/images/cad287f84e959f2a931c39604b5b8983320e1ecf2e010262ecca276692e6fd44.png"
          alt="Fussball Action"
          className="w-full h-full object-cover object-center"
          style={{ minHeight: '120%' }}
        />
      </motion.div>

      {/* Dark Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-slate-900/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-slate-900/20" />

      {/* Stadium Light Flares */}
      <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-lime-300/8 rounded-full blur-[150px]" />
      <div className="absolute top-10 right-10 w-64 h-64 bg-emerald-400/5 rounded-full blur-[100px]" />

      {/* Floating Football Decorations */}
      <motion.div
        className="absolute top-20 right-[15%] text-lime-200/10 hidden lg:block"
        animate={{ y: [0, -20, 0], rotate: [0, 15, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      >
        <FootballIcon size={80} />
      </motion.div>
      <motion.div
        className="absolute bottom-32 right-[8%] text-lime-200/8 hidden lg:block"
        animate={{ y: [0, 15, 0], rotate: [0, -20, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
      >
        <FootballIcon size={50} />
      </motion.div>
      <motion.div
        className="absolute top-[40%] right-[5%] text-white/5 hidden xl:block"
        animate={{ y: [0, 10, 0], rotate: [0, 10, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
      >
        <FootballIcon size={120} />
      </motion.div>

      {/* Field Line Decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />

      <div className="relative z-10 container-main px-4 py-20">
        <div className="max-w-2xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-6"
          >
            <FootballIcon size={16} className="text-lime-200" />
            <span className="text-sm text-slate-300">Die Versicherung für Amateurkicker</span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-6"
            style={{ opacity: contentOpacity }}
          >
            <span className="text-white">WELTNEUHEIT FÜR AMATEURKICKER:</span>
            <br />
            <span className="text-lime-200 italic">Warum die „5.000€ Sofort-Auszahlung"</span>
            <br />
            <span className="text-white">gerade die deutschen Kabinen revolutioniert.</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-lg md:text-xl text-slate-300 max-w-lg mb-8"
          >
            Schluss mit dem Warten auf den Verband. Hol dir deinen Schutz für den Ernstfall – <span className="text-lime-200 font-bold">100% digital</span>, ohne Papierkram und in unter 2 Minuten aktiv.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap gap-4"
          >
            <Button
              onClick={onCtaClick}
              data-testid="hero-cta-btn"
              className="shimmer-btn bg-lime-200 hover:bg-lime-300 text-slate-900 font-bold text-lg px-8 py-6 rounded-full shadow-glow-lime hover:shadow-glow-lime-lg transition-all duration-300 transform hover:scale-105"
            >
              Verfügbarkeit für meine Liga prüfen
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              className="border-slate-500 text-white hover:bg-white/10 px-6 py-6 rounded-full backdrop-blur-sm"
              onClick={() => onCtaClick()}
              data-testid="hero-learn-more-btn"
            >
              Jetzt Auszahlung berechnen
            </Button>
          </motion.div>

          {/* Stats Row */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-wrap gap-8 mt-12"
          >
            {[
              { value: '5.000€', label: 'Max. Auszahlung' },
              { value: '48h', label: 'Bearbeitung' },
              { value: '100%', label: 'Online' },
            ].map((stat, index) => (
              <div key={index} className="text-left">
                <div className="font-display text-3xl font-bold text-lime-200">{stat.value}</div>
                <div className="text-sm text-slate-400">{stat.label}</div>
              </div>
            ))}
          </motion.div>

          {/* Trust Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-8"
          >
            <p className="text-white text-sm font-medium mb-1">Ausgezeichnete Leistungen & Höchste Sicherheit</p>
            <p className="text-slate-500 text-xs mb-3">Testsieger bei Focus Money & AssCompact | Herausragendes Rating durch ascore</p>
            <div className="flex flex-wrap items-center gap-3">
              {Object.values(SEALS).map((src, i) => (
                <img key={i} src={src} alt="Siegel" className="h-12 w-auto opacity-70 hover:opacity-100 transition-opacity" />
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-2"
        >
          <div className="w-1.5 h-3 bg-lime-200 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// Football Field Divider
const FieldDivider = () => (
  <div className="relative py-4" data-testid="field-divider">
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-full h-px bg-gradient-to-r from-transparent via-emerald-600/30 to-transparent" />
    </div>
    <div className="relative flex justify-center">
      <div className="bg-slate-900 px-6">
        <FootballIcon size={20} className="text-emerald-500/40" />
      </div>
    </div>
  </div>
);

// Infographic: "1 von 4" Stat
const InjuryStat = () => (
  <section className="py-20 md:py-28 relative" data-testid="injury-stat">
    <div className="container-main px-4">
      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Visual */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="flex justify-center gap-6">
            {[0, 1, 2, 3].map(i => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.5 }} whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }} transition={{ delay: i * 0.15, type: 'spring' }}>
                <svg viewBox="0 0 40 80" className={`w-12 h-24 md:w-16 md:h-32 ${i === 0 ? 'text-red-500' : 'text-slate-700'}`}>
                  <circle cx="20" cy="12" r="8" fill="currentColor"/>
                  <rect x="14" y="22" width="12" height="24" rx="4" fill="currentColor"/>
                  <rect x="10" y="48" width="8" height="26" rx="3" fill="currentColor"/>
                  <rect x="22" y="48" width="8" height="26" rx="3" fill="currentColor"/>
                  <rect x="6" y="26" width="8" height="4" rx="2" fill="currentColor" transform="rotate(-30 10 28)"/>
                  <rect x="26" y="26" width="8" height="4" rx="2" fill="currentColor" transform="rotate(30 30 28)"/>
                </svg>
              </motion.div>
            ))}
          </motion.div>
          {/* Right: Text */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 mb-4">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              <span className="text-red-400 text-xs uppercase tracking-wider font-medium">Alarmierende Statistik</span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">Jeder <span className="text-red-500">4.</span> Amateurfußballer</h3>
            <p className="text-slate-400 mb-4">verletzt sich pro Saison schwer genug für einen Ausfall. Die meisten davon ohne jede finanzielle Absicherung.</p>
            <p className="text-slate-500 text-sm">Gewöhnliche Vereinsversicherungen zahlen in den meisten Fällen <span className="text-white font-medium">0€ Schmerzensgeld</span> bei Bänderrissen, Muskelrissen oder Knochenbrüchen.</p>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);

// Infographic: Comparison Table
const ComparisonTable = ({ onCta }) => (
  <section className="py-20 md:py-28 relative" data-testid="comparison-table">
    <div className="container-main px-4">
      <SectionHeader tag="Der Unterschied" title="VEREINSHILFE VS." highlight="KICKBACK" subtitle="Warum tausende Spieler wechseln." />
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        className="max-w-3xl mx-auto glass-card rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-3 bg-slate-800/80">
          <div className="p-4 text-slate-400 text-sm font-medium">Leistung</div>
          <div className="p-4 text-slate-400 text-sm font-medium text-center">Vereinshilfe</div>
          <div className="p-4 text-lime-200 text-sm font-bold text-center">KICKBACK</div>
        </div>
        {/* Rows */}
        {[
          { label: 'Auszahlung Bänderriss', old: 'ca. 0€', kickback: '5.000€' },
          { label: 'Auszahlung Knochenbruch', old: 'ca. 250€', kickback: '5.000€' },
          { label: 'Bürokratie', old: 'Hoch (Papier)', kickback: 'Digital (2 Min)' },
          { label: 'Auszahlungsdauer', old: 'Monate', kickback: '48 Stunden' },
        ].map((row, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ delay: i * 0.1 }}
            className={`grid grid-cols-3 ${i % 2 === 0 ? 'bg-slate-800/20' : ''} border-t border-slate-700/30`}>
            <div className="p-4 text-white text-sm">{row.label}</div>
            <div className="p-4 text-slate-500 text-sm text-center">{row.old}</div>
            <div className="p-4 text-lime-200 text-sm font-bold text-center bg-lime-200/5">{row.kickback}</div>
          </motion.div>
        ))}
      </motion.div>
      {/* CTA */}
      <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mt-8">
        <Button onClick={() => onCta && onCta()} className="shimmer-btn bg-lime-200 hover:bg-lime-300 text-slate-900 font-bold px-8 py-3 rounded-full">
          Verfügbarkeit für meine Liga prüfen <ChevronRight className="ml-2 w-4 h-4" />
        </Button>
      </motion.div>
    </div>
  </section>
);

// Infographic: Speed Comparison
const SpeedComparison = () => (
  <section className="py-16 relative" data-testid="speed-comparison">
    <div className="container-main px-4">
      <div className="max-w-2xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="grid grid-cols-2 gap-4">
          {/* Old way */}
          <div className="glass-card rounded-2xl p-6 text-center border-red-500/10">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-red-500/10 flex items-center justify-center">
              <motion.div animate={{ rotate: [0, 360] }} transition={{ duration: 8, repeat: Infinity, ease: 'linear' }} className="text-red-400">
                <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                </svg>
              </motion.div>
            </div>
            <div className="text-red-400 font-display text-2xl font-bold">6-12 Monate</div>
            <p className="text-slate-500 text-xs mt-1">Vereinsversicherung</p>
          </div>
          {/* KICKBACK */}
          <div className="glass-card rounded-2xl p-6 text-center border-lime-200/20">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-lime-200/10 flex items-center justify-center">
              <motion.div initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.5, type: 'spring' }}>
                <Zap className="w-8 h-8 text-lime-200" />
              </motion.div>
            </div>
            <div className="text-lime-200 font-display text-2xl font-bold">48 Stunden</div>
            <p className="text-slate-500 text-xs mt-1">KICKBACK Sofort-Auszahlung</p>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Injury Calculator Section with Interactive Slider
const InjuryCalculator = () => {
  const [selectedStep, setSelectedStep] = useState(2);
  const [sliderValue, setSliderValue] = useState(1);
  const currentInjury = INJURIES[selectedStep];

  // Snap slider to packages: 0=Basic, 1=Pro, 2=Elite
  const pkgMap = [
    { id: 'joker', name: 'Basic', price: 4.99, payout: 1000 },
    { id: 'stammplatz', name: 'Pro', price: 9.99, payout: 3000 },
    { id: 'captain', name: 'Elite', price: 14.99, payout: 5000 },
  ];
  const activePkg = pkgMap[sliderValue];
  const animatedPayout = useCountUp(activePkg.payout, 400);
  
  return (
    <section id="calculator" className="py-24 md:py-32 relative" data-testid="injury-calculator-section">
      {/* Background with subtle grass texture */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800/50 to-slate-900" />
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("https://static.prod-images.emergentagent.com/jobs/2b82e33f-bdf0-4b9d-bca9-c432e89960d3/images/31d155f14f4ff6d405f7a93e29aaad506c3a4224ae7adb7f626a0e34b0384f62.png")`, backgroundSize: '400px', backgroundRepeat: 'repeat' }} />

      {/* Floating football decoration */}
      <motion.div className="absolute top-16 left-8 text-lime-200/6 hidden md:block" animate={{ y: [0, -12, 0], rotate: [0, 8, 0] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}>
        <FootballIcon size={60} />
      </motion.div>
      <motion.div className="absolute bottom-16 right-12 text-emerald-400/5 hidden md:block" animate={{ y: [0, 10, 0], rotate: [0, -12, 0] }} transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 3 }}>
        <FootballIcon size={45} />
      </motion.div>
      
      <div className="container-main px-4 relative z-10">
        <SectionHeader tag="Kalkulator" title="INJURY" highlight="TO CASH" subtitle="Schiebe den Regler und sieh sofort, was du bei einer Verletzung erhältst." />
        
        {/* Calculator Card */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-4xl mx-auto glass-card rounded-3xl p-8 md:p-12">

          {/* Premium Slider */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-sm">Deine monatliche Investition</span>
              <motion.div key={activePkg.price} initial={{ scale: 1.3 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400 }}
                className="font-display text-2xl font-bold text-lime-200">
                <span className="text-slate-500 text-sm line-through mr-2">{(activePkg.price * 2).toFixed(2).replace('.', ',')}€</span>
                {activePkg.price.toFixed(2).replace('.', ',')}€<span className="text-slate-500 text-sm font-normal"> /Monat</span>
              </motion.div>
            </div>

            {/* Slider Track */}
            <div className="relative pt-2 pb-6">
              <input
                type="range" min="0" max="2" step="1" value={sliderValue}
                onChange={(e) => setSliderValue(parseInt(e.target.value))}
                className="w-full h-3 appearance-none bg-slate-800 rounded-full cursor-pointer relative z-10"
                style={{
                  background: `linear-gradient(to right, #D9F99D 0%, #D9F99D ${sliderValue * 50}%, #1E293B ${sliderValue * 50}%, #1E293B 100%)`
                }}
                data-testid="price-slider"
              />
              {/* Snap Points */}
              <div className="absolute top-[14px] left-0 right-0 flex justify-between pointer-events-none px-[2px]">
                {pkgMap.map((p, i) => (
                  <div key={i} className={`w-4 h-4 rounded-full border-2 transition-all ${i <= sliderValue ? 'bg-lime-200 border-lime-200 shadow-glow-lime' : 'bg-slate-700 border-slate-600'}`} />
                ))}
              </div>
              {/* Labels */}
              <div className="flex justify-between mt-3">
                {pkgMap.map((p, i) => (
                  <button key={i} onClick={() => setSliderValue(i)}
                    className={`text-xs font-medium transition-all ${i === sliderValue ? 'text-lime-200' : 'text-slate-500 hover:text-slate-300'}`}>
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left: Football Injury Visualization */}
            <div className="relative h-80 md:h-96 flex items-center justify-center">
              <div className="relative w-full h-full flex items-center justify-center">
                <motion.div className="relative" animate={{ y: [0, -8, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}>
                  <svg viewBox="0 0 200 200" className="w-40 h-40 md:w-56 md:h-56">
                    <defs>
                      <radialGradient id="ballGrad" cx="40%" cy="35%"><stop offset="0%" stopColor="#334155"/><stop offset="100%" stopColor="#0F172A"/></radialGradient>
                    </defs>
                    <circle cx="100" cy="100" r="90" fill="url(#ballGrad)" stroke="#475569" strokeWidth="2"/>
                    <polygon points="100,30 130,55 120,90 80,90 70,55" fill="#1E293B" stroke="#64748B" strokeWidth="1.5"/>
                    <polygon points="155,75 170,110 150,140 120,130 125,95" fill="#1E293B" stroke="#64748B" strokeWidth="1.5"/>
                    <polygon points="45,75 30,110 50,140 80,130 75,95" fill="#1E293B" stroke="#64748B" strokeWidth="1.5"/>
                    <polygon points="65,150 80,175 120,175 135,150 110,135 90,135" fill="#1E293B" stroke="#64748B" strokeWidth="1.5"/>
                    <line x1="100" y1="30" x2="100" y2="10" stroke="#64748B" strokeWidth="1" opacity="0.4"/>
                    <line x1="130" y1="55" x2="155" y2="42" stroke="#64748B" strokeWidth="1" opacity="0.4"/>
                    <line x1="70" y1="55" x2="45" y2="42" stroke="#64748B" strokeWidth="1" opacity="0.4"/>
                    <line x1="170" y1="110" x2="190" y2="110" stroke="#64748B" strokeWidth="1" opacity="0.4"/>
                    <line x1="30" y1="110" x2="10" y2="110" stroke="#64748B" strokeWidth="1" opacity="0.4"/>
                  </svg>
                  <motion.div key={currentInjury.zone} initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 md:w-28 md:h-28 rounded-full border-2 border-lime-200/50 bg-lime-200/10 animate-pulse-glow flex items-center justify-center">
                      <Activity className="w-8 h-8 md:w-10 md:h-10 text-lime-200" />
                    </div>
                  </motion.div>
                </motion.div>

                {INJURIES.map((injury, idx) => {
                  const angle = (idx / INJURIES.length) * Math.PI * 2 - Math.PI / 2;
                  const radius = 140;
                  const x = 50 + Math.cos(angle) * (radius / 3.2);
                  const y = 50 + Math.sin(angle) * (radius / 3.2);
                  const isActive = selectedStep === idx;
                  return (
                    <motion.button key={injury.id}
                      className={`absolute text-xs font-medium px-2 py-1 rounded-full transition-all whitespace-nowrap ${isActive ? 'bg-lime-200 text-slate-900 shadow-glow-lime scale-110' : 'bg-slate-800/80 text-slate-400 hover:text-white border border-slate-700'}`}
                      style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }}
                      onClick={() => setSelectedStep(idx)}
                      animate={isActive ? { scale: [1, 1.05, 1] } : {}}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      data-testid={`injury-label-${idx}`}
                    >{injury.name}</motion.button>
                  );
                })}
                <div className="absolute inset-0 bg-lime-200/5 blur-3xl rounded-full pointer-events-none" />
              </div>
            </div>
            
            {/* Right: Payout Display */}
            <div>
              {/* Current Injury */}
              <motion.div key={currentInjury.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="mb-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/80 border border-lime-200/30">
                  <Activity className="w-4 h-4 text-lime-200" />
                  <span className="text-white font-medium">{currentInjury.name}</span>
                </div>
                <p className="text-slate-400 text-sm mt-2">{currentInjury.description}</p>
              </motion.div>
              
              {/* Payout Display */}
              <div className="mb-6">
                <div className="text-slate-400 text-sm mb-1">Deine Sofort-Auszahlung mit <span className="text-lime-200 font-medium">{activePkg.name}</span></div>
                <motion.div key={activePkg.payout} initial={{ scale: 0.9, opacity: 0.5 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 300 }}
                  className="font-display text-6xl md:text-7xl font-bold text-lime-200 text-glow-lime" data-testid="payout-amount">
                  +{animatedPayout.toLocaleString('de-DE')}€
                </motion.div>
                <div className="text-slate-500 text-sm mt-1">Direkt auf dein Konto – egal welche Verletzung</div>
              </div>

              {/* Quick Payout Comparison */}
              <div className="grid grid-cols-3 gap-2 mb-6">
                {pkgMap.map((p, i) => (
                  <motion.div key={p.id}
                    className={`p-3 rounded-xl text-center cursor-pointer transition-all ${i === sliderValue ? 'bg-lime-200/15 border border-lime-200/40' : 'bg-slate-800/50 border border-slate-700/50 hover:border-slate-600'}`}
                    onClick={() => setSliderValue(i)}
                    whileHover={{ scale: 1.03 }}
                  >
                    <div className={`text-xs mb-1 ${i === sliderValue ? 'text-lime-200' : 'text-slate-500'}`}>{p.name}</div>
                    <div className={`font-display text-lg font-bold ${i === sliderValue ? 'text-white' : 'text-slate-400'}`}>{p.payout.toLocaleString('de-DE')}€</div>
                    <div className={`text-[10px] ${i === sliderValue ? 'text-lime-200/70' : 'text-slate-600'}`}>{p.price.toFixed(2).replace('.', ',')}€/mtl.</div>
                  </motion.div>
                ))}
              </div>
              
              {/* Value Proposition */}
              <div className="p-4 rounded-xl bg-slate-800/50 border border-lime-200/20">
                <div className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-lime-200 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-white">Kein Papierkram</div>
                    <div className="text-sm text-slate-400">Foto vom Befund hochladen, Geld erhalten.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section className="py-24 md:py-32 relative overflow-hidden" data-testid="testimonials-section">
      {/* Football field line decorations */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200px] h-[200px] rounded-full border border-emerald-500/10" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[100px] h-[100px] rounded-full border border-emerald-500/8" />

      <div className="container-main px-4">
        {/* Section Header */}
        <SectionHeader tag="Erfahrungen" title="VOM PLATZ" highlight="FÜR DEN PLATZ" subtitle="Echte Spieler. Echte Verletzungen. Echtes Geld." />
        
        {/* Trading Card Style Testimonials */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {TESTIMONIALS.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2, duration: 0.5 }}
              whileHover={{ y: -10, scale: 1.02 }}
              className="relative group card-tilt"
              data-testid={`testimonial-card-${testimonial.id}`}
            >
              {/* Card */}
              <div className="relative bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl overflow-hidden border border-slate-700 group-hover:border-lime-200/30 transition-all duration-300">
                {/* Top Badge */}
                <div className="absolute top-4 right-4 z-10">
                  <div className="bg-lime-200 text-slate-900 text-xs font-bold px-3 py-1 rounded-full">
                    {testimonial.payout}
                  </div>
                </div>
                
                {/* Player Image */}
                <div className="h-48 overflow-hidden">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    style={{ filter: 'brightness(0.85) contrast(1.1)' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
                </div>
                
                {/* Content */}
                <div className="p-6">
                  {/* Quote */}
                  <div className="relative mb-4">
                    <Quote className="absolute -top-2 -left-2 w-8 h-8 text-lime-200/20" />
                    <p className="text-slate-300 text-sm italic pl-4">
                      "{testimonial.quote}"
                    </p>
                  </div>
                  
                  {/* Player Info */}
                  <div className="flex items-center justify-between border-t border-slate-700 pt-4">
                    <div>
                      <p className="text-white font-bold">{testimonial.name}</p>
                      <p className="text-slate-500 text-xs">{testimonial.team}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lime-200 text-xs font-medium">{testimonial.injury}</p>
                    </div>
                  </div>
                </div>
                
                {/* Trading Card Border Effect */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-lime-200/50 to-transparent" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Package Selection Component - Snapping Slider
const PackageSelection = ({ onSelectPackage, selectedPackage }) => {
  const [activeIdx, setActiveIdx] = useState(1); // Center Stage: Pro
  const activePkg = PACKAGES[activeIdx];
  const stars = activeIdx === 0 ? 3 : activeIdx === 1 ? 4 : 5;

  return (
    <section className="py-24 md:py-32 relative" data-testid="package-section">
      <div className="absolute inset-0 opacity-[0.02]"
        style={{ backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,0.3) 40px, rgba(255,255,255,0.3) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(255,255,255,0.3) 40px, rgba(255,255,255,0.3) 41px)` }}
      />
      <motion.div className="absolute top-20 right-16 text-lime-200/5 hidden lg:block" animate={{ y: [0, -15, 0], rotate: [0, 20, 0] }} transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}>
        <FootballIcon size={70} />
      </motion.div>

      <div className="container-main px-4">
        <SectionHeader tag="Pakete" title="WÄHLE DEIN" highlight="PAKET" subtitle="Schiebe den Regler und finde deinen perfekten Schutz." />

        <div className="max-w-lg mx-auto">
          {/* Snapping Slider */}
          <div className="mb-8">
            <div className="relative pt-2 pb-8">
              <input type="range" min="0" max="2" step="1" value={activeIdx}
                onChange={(e) => setActiveIdx(parseInt(e.target.value))}
                className="w-full h-4 appearance-none bg-slate-800 rounded-full cursor-pointer relative z-10"
                style={{ background: `linear-gradient(to right, #D9F99D 0%, #D9F99D ${activeIdx * 50}%, #1E293B ${activeIdx * 50}%, #1E293B 100%)` }}
                data-testid="package-slider"
              />
              <div className="absolute top-[18px] left-0 right-0 flex justify-between pointer-events-none px-[2px]">
                {PACKAGES.map((_, i) => (
                  <div key={i} className={`w-5 h-5 rounded-full border-2 transition-all ${i <= activeIdx ? 'bg-lime-200 border-lime-200 shadow-glow-lime' : 'bg-slate-700 border-slate-600'}`} />
                ))}
              </div>
              <div className="flex justify-between mt-4">
                {PACKAGES.map((p, i) => (
                  <button key={i} onClick={() => setActiveIdx(i)}
                    className={`text-sm font-medium transition-all ${i === activeIdx ? 'text-lime-200 font-bold' : 'text-slate-500 hover:text-slate-300'}`}>
                    {p.name}<br/><span className="text-xs font-normal">{p.price.toFixed(2).replace('.',',')}€</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Dynamic Package Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activePkg.id}
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -60 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="glass-card rounded-3xl p-8 md:p-10 gradient-border relative"
              data-testid={`package-card-${activePkg.id}`}
            >
              {/* Badge */}
              {activePkg.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="inline-flex items-center gap-1 px-4 py-1 rounded-full bg-lime-200 text-slate-900 text-xs font-bold shadow-glow-lime">
                    <Trophy className="w-3 h-3" /> Preis-Leistungs-Sieger
                  </div>
                </div>
              )}

              {/* Stars */}
              <div className="flex justify-center gap-1 mb-3 mt-2">
                {Array.from({ length: stars }).map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-current text-lime-200" />
                ))}
              </div>

              {/* Package Name */}
              <h3 className="font-display text-3xl md:text-4xl font-bold text-white text-center mb-2">
                DEIN SCHUTZ: <span className="text-lime-200">{activePkg.name.toUpperCase()}</span>
              </h3>

              {/* Payout */}
              <motion.div key={`pay-${activePkg.payout}`} initial={{ scale: 0.8 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 300 }}
                className="text-center mb-6">
                <div className="font-display text-5xl md:text-6xl font-bold text-lime-200 text-glow-lime">
                  {activePkg.payout.toLocaleString('de-DE')}€
                </div>
                <div className="text-slate-400 text-sm mt-1">SOFORT-CASH bei Verletzung</div>
              </motion.div>

              {/* Features */}
              <ul className="space-y-3 mb-8 max-w-sm mx-auto">
                {activePkg.features.map((feature, i) => (
                  <motion.li key={`${activePkg.id}-${i}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                    className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-lime-200 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">{feature}</span>
                  </motion.li>
                ))}
              </ul>

              {/* Price + CTA */}
              <div className="text-center">
                <div className="flex items-end justify-center gap-2 mb-4">
                  <span className="text-slate-500 text-xl line-through">{(activePkg.price * 2).toFixed(2).replace('.', ',')}€</span>
                  <motion.span key={`price-${activePkg.price}`} initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                    className="font-display text-4xl font-bold text-lime-200">
                    {activePkg.price.toFixed(2).replace('.', ',')}€
                  </motion.span>
                  <span className="text-slate-500 mb-1 text-lg">/ Monat</span>
                </div>
                <p className="text-lime-200/70 text-xs mb-3 font-medium">50% Rabatt · Code KICKBACK50 bereits angewendet</p>
                <Button
                  onClick={() => onSelectPackage(activePkg.id)}
                  className="shimmer-btn bg-lime-200 hover:bg-lime-300 text-slate-900 font-bold text-lg px-10 py-4 rounded-full shadow-glow-lime hover:shadow-glow-lime-lg transition-all"
                  data-testid={`select-package-${activePkg.id}`}
                >
                  VERFÜGBARKEIT PRÜFEN
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Mini comparison below */}
          <div className="flex justify-center gap-3 mt-6">
            {PACKAGES.map((p, i) => (
              <button key={p.id} onClick={() => setActiveIdx(i)}
                className={`px-3 py-1.5 rounded-full text-xs transition-all ${i === activeIdx ? 'bg-lime-200/15 text-lime-200 border border-lime-200/30' : 'bg-slate-800/50 text-slate-500 hover:text-slate-300'}`}>
                {p.name} · {p.payout.toLocaleString('de-DE')}€
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  return (
    <section className="py-24 md:py-32 relative" data-testid="faq-section">
      <div className="container-main px-4">
        {/* Section Header */}
        <SectionHeader tag="Support" title="HÄUFIGE" highlight="FRAGEN" subtitle="Alles was du wissen musst." />
        
        {/* FAQ Accordion */}
        <div className="max-w-2xl mx-auto space-y-4">
          {FAQ_DATA.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`rounded-xl overflow-hidden transition-all duration-300 ${
                openIndex === index 
                  ? 'bg-slate-800 border-2 border-lime-200/50 shadow-[0_0_20px_rgba(217,249,157,0.15)]' 
                  : 'bg-slate-800/50 border-2 border-slate-700'
              }`}
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left"
              >
                <span className={`font-medium ${openIndex === index ? 'text-lime-200' : 'text-white'}`}>
                  {faq.q}
                </span>
                <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${
                  openIndex === index ? 'rotate-180 text-lime-200' : 'text-slate-400'
                }`} />
              </button>
              
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-5 text-slate-400">
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Team Section
const TeamSection = () => {
  return (
    <section className="py-24 md:py-32 relative overflow-hidden" data-testid="team-section">
      <div className="container-main px-4">
        <SectionHeader tag="Über uns" title="VON KICKERN" highlight="FÜR KICKER" subtitle="Wir sind selbst Amateurfußballer. Wir wissen, wie es ist, verletzt zu sein." />

        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl overflow-hidden"
          >
            {/* Team Photo */}
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/h0lpjful_unnamed%20-%202026-02-21T103243.506.jpg"
                alt="Das KICKBACK Team auf dem Fußballplatz"
                className="w-full h-72 md:h-[420px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/30 to-transparent" />
            </div>

            {/* Content overlay */}
            <div className="relative -mt-32 md:-mt-40 p-6 md:p-10 z-10">
              <div className="glass-card rounded-2xl p-6 md:p-8 max-w-2xl">
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="text-slate-300 leading-relaxed mb-4"
                >
                  Wir haben <strong className="text-white">KICKBACK</strong> gegründet, weil wir selbst erlebt haben, was eine Verletzung auf dem Platz bedeutet – nicht nur körperlich, sondern auch finanziell. Die Idee war simpel: <span className="text-lime-200">Eine Versicherung, die so unkompliziert ist wie ein Doppelpass.</span>
                </motion.p>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="text-slate-400 text-sm"
                >
                  Kein Kleingedrucktes, kein Papierkram. Verletzt? Befund hochladen. Geld erhalten. So einfach sollte Versicherung sein.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  className="flex flex-wrap gap-6 mt-6 pt-6 border-t border-slate-700/50"
                >
                  {[
                    { value: '2023', label: 'Gegründet' },
                    { value: '4.200+', label: 'Versicherte Spieler' },
                    { value: '98%', label: 'Zufriedenheit' },
                  ].map((stat, i) => (
                    <div key={i}>
                      <div className="font-display text-xl font-bold text-lime-200">{stat.value}</div>
                      <div className="text-xs text-slate-500">{stat.label}</div>
                    </div>
                  ))}
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Presseartikel Section
const PressSection = ({ onCta }) => {
  return (
    <section className="py-24 md:py-32 relative overflow-hidden" data-testid="press-section">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800/30 to-slate-900" />

      {/* Football decoration */}
      <motion.div
        className="absolute top-12 left-10 text-emerald-500/5 hidden md:block"
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
      >
        <FootballIcon size={90} />
      </motion.div>
      
      <div className="container-main px-4 relative z-10">
        {/* Section Header */}
        <SectionHeader tag="Bekannt aus" title="IN DER" highlight="PRESSE" />

        {/* Media Logos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-center mb-12"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/cdos3vof_Design%20ohne%20Titel%20-%202026-02-20T193133.387.png"
            alt="Bekannt aus Kicker und Handelsblatt"
            className="h-16 md:h-20 w-auto opacity-80 hover:opacity-100 transition-opacity"
          />
        </motion.div>
        
        {/* Press Article Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto"
        >
          <div className="glass-card rounded-3xl overflow-hidden">
            {/* Article Header Image */}
            <div className="relative h-48 md:h-64 bg-gradient-to-br from-slate-800 to-slate-700">
              <img 
                src="https://images.pexels.com/photos/47343/the-ball-stadion-horn-corner-47343.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Fussball auf dem Spielfeld"
                className="w-full h-full object-cover opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
              <div className="absolute bottom-4 left-6 right-6">
                <span className="inline-block px-3 py-1 bg-lime-200 text-slate-900 text-xs font-bold rounded-full mb-2">
                  PRESSEARTIKEL
                </span>
                <h3 className="font-display text-xl md:text-2xl font-bold text-white leading-tight">
                  Mit diesem Trick bekommst du 5.000€ bei einem Knochenbruch auf dein Konto.
                </h3>
              </div>
            </div>
            
            {/* Article Content */}
            <div className="p-6 md:p-8">
              {/* Author info */}
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-700">
                <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center">
                  <span className="text-lime-200 font-bold">JS</span>
                </div>
                <div>
                  <p className="text-white font-medium">Jessica Schneider</p>
                  <p className="text-slate-500 text-sm">Chefredakteurin • 09.03.2023</p>
                </div>
              </div>
              
              {/* Article excerpt */}
              <div className="space-y-4 text-slate-300">
                <p className="font-medium text-white text-lg">Dieser Trick verrät, was Du dagegen tun kannst</p>

                <p className="text-sm">
                  Was Krankenkassen nicht verraten: Die simple Fussball-Versicherung reicht aus, um die hohen Kosten abzufedern und <strong className="text-white">ON-TOP sogar noch Schmerzensgeld zu erhalten</strong>. Besonders beliebt sind folgende Leistungen:
                </p>

                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-lime-200 flex-shrink-0" />
                    <span>Schmerzensgeld nach Knochenbruch</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-lime-200 flex-shrink-0" />
                    <span>Schmerzensgeld nach Sehnen-, Muskel- oder Bänderrissen</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-lime-200 flex-shrink-0" />
                    <span>Zuzahlungen für ambulante Rehabilitation</span>
                  </li>
                </ul>

                <p className="text-sm">
                  Kundenliebling sind die Tarife der <strong className="text-lime-200">Fussballer-Versicherung.de</strong>. Bereits Tausende Kunden nutzen die einzigartige Absicherung und sparen damit bares Geld.
                </p>

                <div className="flex items-start gap-3 p-4 rounded-xl bg-lime-200/10 border border-lime-200/20">
                  <Zap className="w-5 h-5 text-lime-200 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-lime-200 mb-1">Die Lösung: Überraschend günstig</p>
                    <p className="text-sm">Die Versicherung übernimmt die vollständigen Kosten für gesetzliche Zuzahlungen, z.B. ambulante Reha-Maßnahmen. Die Tarife beginnen bereits ab günstigen <strong className="text-white">4,99€ pro Monat</strong>.</p>
                  </div>
                </div>

                <p className="text-sm">
                  Einzigartig bei Fussballer-versicherung.de: Mit der <strong className="text-white">Sofortleistung ohne Wartezeiten</strong> sparst Du bares Geld, auch wenn Dir schon morgen auf dem Platz etwas passiert. All das ohne Wartezeit!
                </p>

                <p className="text-sm">
                  Es spielt keine Rolle bei welcher Krankenversicherung Du momentan versichert bist, da die Tarife komplett unabhängig sind und sich somit nichts bei Deiner aktuellen Krankenversicherung ändert.
                </p>

                <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <Activity className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-amber-300 mb-1">Begrenzte Verfügbarkeit</p>
                    <p className="text-sm">Wegen der großen Nachfrage können derzeit nur begrenzt Neukunden aufgenommen werden. Überprüfe daher jetzt, ob Du Dich noch absichern kannst.</p>
                  </div>
                </div>
              </div>
              
              {/* CTA */}
              <div className="mt-6 pt-6 border-t border-slate-700">
                <Button 
                  onClick={() => onCta && onCta()}
                  className="w-full shimmer-btn bg-lime-200 hover:bg-lime-300 text-slate-900 font-bold py-4 rounded-full"
                  data-testid="press-cta-btn"
                >
                  Verfügbarkeit für meine Liga prüfen
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// German Bank Detection from IBAN BLZ
const detectBank = (iban) => {
  const clean = iban.replace(/\s/g, '').toUpperCase();
  if (clean.length < 8) return null;
  const blz = clean.substring(4, 12);
  const prefix = blz.substring(0, 3);
  const bankMap = {
    '100': 'Bundesbank', '120': 'Commerzbank', '200': 'Hamburger Sparkasse',
    '250': 'Norddeutsche LB', '300': 'Stadtsparkasse Düsseldorf', '370': 'Commerzbank',
    '380': 'Commerzbank', '390': 'Commerzbank', '400': 'Commerzbank',
    '430': 'Stadtsparkasse Bochum', '440': 'Dortmunder Volksbank',
    '500': 'Landesbank Hessen', '502': 'ING', '503': 'Deutsche Bank',
    '510': 'Nassauische Sparkasse', '550': 'Sparkasse Mainz',
    '600': 'Baden-Württembergische Bank', '640': 'Commerzbank',
    '660': 'Sparkasse Karlsruhe', '680': 'Sparkasse Freiburg',
    '700': 'Bayerische Landesbank', '701': 'Deutsche Bundesbank',
    '720': 'UniCredit', '760': 'Sparkasse Nürnberg',
  };
  const prefix2 = blz.substring(0, 4);
  const detailedMap = {
    '3704': 'Commerzbank', '5001': 'ING-DiBa', '1001': 'Bundesbank',
    '2105': 'Hamburger Sparkasse', '7002': 'HypoVereinsbank',
    '5003': 'Deutsche Bank', '6030': 'BW-Bank', '1007': 'Deutsche Bank',
    '2004': 'Hamburger Volksbank', '3005': 'Stadtsparkasse Düsseldorf',
  };
  return detailedMap[prefix2] || bankMap[prefix] || (clean.length >= 8 ? 'Deutsche Bank' : null);
};

// Social Proof Ticker
const SOCIAL_PROOF = [
  { name: 'Luca aus Köln', pkg: 'Pro' },
  { name: 'Tim aus München', pkg: 'Elite' },
  { name: 'Marco aus Hamburg', pkg: 'Basic' },
  { name: 'Jan aus Berlin', pkg: 'Pro' },
  { name: 'Felix aus Frankfurt', pkg: 'Pro' },
  { name: 'Niklas aus Dortmund', pkg: 'Elite' },
];

const SocialProofTicker = () => {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setCurrent(p => (p + 1) % SOCIAL_PROOF.length), 12000);
    return () => clearInterval(interval);
  }, []);
  const proof = SOCIAL_PROOF[current];
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={current}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="flex items-center justify-center gap-2 text-xs text-slate-500 py-3"
        data-testid="social-proof-ticker"
      >
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        <span>Gerade eben: <strong className="text-slate-400">{proof.name}</strong> hat das <strong className="text-lime-200/70">{proof.pkg}</strong>-Paket abgeschlossen.</span>
      </motion.div>
    </AnimatePresence>
  );
};

// JOB list for autocomplete
const JOB_LIST = [
  'Handwerker/in', 'Bürokaufmann/-frau', 'Student/in', 'Ingenieur/in', 'Lehrer/in',
  'Krankenpfleger/in', 'Koch/Köchin', 'IT-Fachkraft', 'Kaufmann/-frau', 'Elektriker/in',
  'Mechaniker/in', 'Verkäufer/in', 'Lagerarbeiter/in', 'Erzieher/in', 'Selbständig',
  'Schüler/in', 'Auszubildende/r', 'Beamter/Beamtin', 'Polizist/in', 'Sonstige',
];

// Checkout Flow Component
const CheckoutForm = ({ selectedPackage: initialPackage, onComplete }) => {
  const [step, setStep] = useState(1);
  const [selectedPackage, setSelectedPkg] = useState(initialPackage);
  const [formData, setFormData] = useState({
    protectWho: '',
    club: '',
    clubSearch: '',
    birthYear: 1995,
    birthMonth: 6,
    birthDay: 15,
    job: '',
    jobSearch: '',
    customJob: '',
    healthCheck: null,
    paymentInterval: 'monthly',
    addon: false,
    iban: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    zip: '',
    city: '',
    isAlsoPolicyholder: true,
    policyholderName: '',
    policyholderAddress: '',
    priorAccidents: null,
    couponActive: false,
    couponCode: 'KICKBACK50',
  });
  const [ibanValid, setIbanValid] = useState(null);
  const [detectedBank, setDetectedBank] = useState(null);
  const [ibanProgress, setIbanProgress] = useState(0);
  const [ageChecking, setAgeChecking] = useState(false);
  const [ageOk, setAgeOk] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitPhase, setSubmitPhase] = useState(''); // '' | 'validating' | 'activating' | 'done'
  const [showJobList, setShowJobList] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [legal1, setLegal1] = useState(false);
  const [legal2, setLegal2] = useState(false);
  const [clubResults, setClubResults] = useState([]);
  const [showClubList, setShowClubList] = useState(false);
  const [clubManual, setClubManual] = useState(false);

  // Check if legal text needs scrolling - if not, auto-enable checkbox
  // Sync legalAccepted with both checkboxes
  useEffect(() => {
    setLegalAccepted(legal1 && legal2);
  }, [legal1, legal2]);

  const totalSteps = 6;
  const progress = (step / totalSteps) * 100;
  const pkg = PACKAGES.find(p => p.id === selectedPackage);

  const filteredJobs = JOB_LIST.filter(j =>
    j.toLowerCase().includes(formData.jobSearch.toLowerCase())
  );

  const searchClubs = async (query) => {
    if (query.length < 2) { setClubResults([]); return; }
    try {
      const res = await axios.get(`${API}/clubs/search?q=${encodeURIComponent(query)}`);
      setClubResults(res.data.clubs || []);
      setShowClubList(true);
    } catch (e) { setClubResults([]); }
  };

  const handleIbanChange = (e) => {
    const formatted = formatIBAN(e.target.value);
    if (formatted.replace(/\s/g, '').length <= 22) {
      setFormData({ ...formData, iban: formatted });
      const cleanLen = formatted.replace(/\s/g, '').length;
      setIbanProgress(Math.min((cleanLen / 22) * 100, 100));
      if (cleanLen >= 8) setDetectedBank(detectBank(formatted));
      else setDetectedBank(null);
      if (cleanLen >= 20) setIbanValid(validateIBAN(formatted));
      else setIbanValid(null);
    }
  };

  const simulateAgeCheck = () => {
    setAgeChecking(true);
    setAgeOk(null);
    setTimeout(() => {
      const age = new Date().getFullYear() - formData.birthYear;
      setAgeOk(age >= 16 && age <= 65);
      setAgeChecking(false);
    }, 800);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitPhase('validating');
    await new Promise(r => setTimeout(r, 1200));
    setSubmitPhase('activating');
    try {
      const submitData = { ...formData };
      if (submitData.job === 'Sonstige' && submitData.customJob) {
        submitData.job = submitData.customJob;
      }
      delete submitData.jobSearch;
      delete submitData.customJob;
      delete submitData.clubSearch;
      await axios.post(`${API}/insurance/submit`, {
        ...submitData,
        packageId: selectedPackage,
        packageName: pkg?.name,
        monthlyPrice: (pkg?.price || 0) + (submitData.addon ? 1.99 : 0),
        addon: submitData.addon || false,
      });
      // Lead tracking only via server-side CAPI (no duplicate frontend pixel)
      trackEvent('checkout_complete', { packageId: selectedPackage });
      setSubmitPhase('done');
      await new Promise(r => setTimeout(r, 800));
      onComplete({ firstName: formData.firstName, lastName: formData.lastName, club: formData.club, packageName: pkg?.name, payout: pkg?.payout });
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
      setSubmitPhase('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.protectWho !== '' && formData.club !== '';
      case 2: return formData.birthYear && ageOk === true;
      case 3: return true; // Package selection - always can proceed (pre-selected)
      case 4: {
        const jobValid = formData.job === 'Sonstige' ? formData.customJob?.trim() : formData.job;
        return jobValid && formData.healthCheck !== null;
      }
      case 5: return true;
      case 6: {
        const baseValid = formData.firstName && formData.lastName && formData.email && formData.phone && formData.street && formData.zip && formData.city && legalAccepted && ibanValid === true;
        const phValid = formData.isAlsoPolicyholder || (formData.policyholderName && formData.policyholderAddress);
        return baseValid && phValid;
      }
      default: return false;
    }
  };

  const [microReward, setMicroReward] = useState('');
  const [showExpertCheck, setShowExpertCheck] = useState(false);

  const nextStep = () => {
    if (canProceed() && step < totalSteps) {
      const next = step + 1;
      // Set yearly as default when reaching payment step
      // Keep monthly as default - user chooses in step 4
      // Micro-reward messages
      const rewards = {
        2: 'Check erfolgreich! Weiter gehts.',
        3: 'Alter bestätigt. Jetzt Paket wählen.',
        4: 'Top-Konditionen gesichert. Kurzer Risikocheck.',
        5: 'Diese Antwort qualifiziert dich für die 48h-Sofort-Auszahlung.',
        6: 'Fast geschafft!',
      };
      setMicroReward(rewards[next] || '');
      setTimeout(() => setMicroReward(''), 2500);
      // Expert check animation before step 4 (risk check)
      if (next === 4) {
        setShowExpertCheck(true);
        setTimeout(() => { setShowExpertCheck(false); setStep(next); }, 2000);
      } else {
        setStep(next);
      }
      trackEvent('checkout_step', { step: next, packageId: selectedPackage });
      // Meta Micro-Events
      if (typeof window.fbq === 'function') {
        if (next === 3) window.fbq('track', 'AddToCart', { content_name: pkg?.name, currency: 'EUR', value: pkg?.price });
        if (next === 5) window.fbq('track', 'AddPaymentInfo');
        if (next === 6) window.fbq('track', 'InitiateCheckout', { content_name: pkg?.name, currency: 'EUR', value: pkg?.price });
      }
    }
  };
  const prevStep = () => { if (step > 1) setStep(step - 1); };

  const years = Array.from({ length: 50 }, (_, i) => 2008 - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  // Quiz progress messages
  const progressMsg = (() => {
    if (step === 1) return { text: 'Nur 2 Minuten für deinen Testsieger-Schutz (Focus Money).', pct: 56 };
    if (step === 2) return { text: 'Weiter so! Nur noch wenige Angaben.', pct: 60 };
    if (step === 3) return { text: 'Wähle deinen Schutz – welches Paket passt zu dir?', pct: 68 };
    if (step === 4) return { text: `Glückwunsch! Du erfüllst die Kriterien für den Platin-Tarif (ascore: Herausragend).`, pct: 76 };
    if (step === 5) return { text: 'Noch ein optionales Upgrade, dann bist du durch!', pct: 85 };
    if (step === 6) return { text: 'Fast geschafft! Deine Daten sind bei uns so sicher wie bei deiner Bank.', pct: 93 };
    return { text: '', pct: 100 };
  })();

  return (
    <div className="fixed inset-0 z-50 bg-slate-900 overflow-y-auto" data-testid="checkout-section">
      {/* Urgency Banner */}
      <div className="bg-gradient-to-r from-[#FFD700] to-[#FF8C00] text-black text-center py-2 px-4 text-xs font-medium">
        <span className="inline-flex items-center gap-1.5"><Zap className="w-3 h-3" /> 50% RABATT aktiv! Endet in <CountdownBadge /> · Code KICKBACK50 angewendet.</span>
      </div>

      {/* Fullscreen Quiz Header */}
      <div className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <img src="/kickback-logo.png" alt="KICKBACK" className="h-6 w-auto" />
              <span className="text-slate-500 text-xs hidden sm:inline">Quick-Check</span>
            </div>
            <div className="flex items-center gap-3">
              {/* Live Score */}
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all ${
                step >= 5 ? 'bg-lime-200/15 text-lime-200 border border-lime-200/30' :
                step >= 3 ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' :
                'bg-red-500/15 text-red-400 border border-red-500/30'
              }`}>
                <div className={`w-1.5 h-1.5 rounded-full ${step >= 5 ? 'bg-lime-200' : step >= 3 ? 'bg-amber-400' : 'bg-red-400'} animate-pulse`} />
                {step >= 5 ? 'Schutz: Aktiv' : step >= 3 ? 'Schutz: Fast bereit' : 'Risiko: Ungeschützt'}
              </div>
              <button onClick={() => onComplete(null)} className="text-slate-600 hover:text-white text-xs transition-colors" data-testid="quiz-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <motion.div className={`h-full rounded-full ${step >= 5 ? 'bg-gradient-to-r from-lime-300 to-lime-200' : step >= 3 ? 'bg-gradient-to-r from-amber-500 to-lime-300' : 'bg-gradient-to-r from-red-500 to-amber-400'}`} animate={{ width: `${progressMsg.pct}%` }} transition={{ duration: 0.5 }} />
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <p className="text-slate-400 text-xs">{progressMsg.text}</p>
            <p className="text-slate-600 text-[10px] flex items-center gap-1"><Shield className="w-3 h-3 text-emerald-400/50" />SSL & DSGVO</p>
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8">
        {/* Step Seal */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-center gap-2 mb-6">
            <img src={SEALS.focusMoney} alt="Focus Money" className="h-10 opacity-70" />
            <span className="text-slate-500 text-xs">Ausgezeichnete Leistungen</span>
          </motion.div>
        )}
        {step === 3 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-center gap-2 mb-6">
            <img src={SEALS.focusMoney} alt="Focus Money" className="h-10 opacity-70" />
            <span className="text-slate-500 text-xs">Ausgezeichnete Leistungen</span>
          </motion.div>
        )}
        {step === 4 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-center gap-2 mb-6">
            <img src={SEALS.asscompact2020} alt="Platz 1" className="h-10 opacity-70" />
            <span className="text-slate-500 text-xs">Platz 1 – Schaden/Unfall</span>
          </motion.div>
        )}
        {step === 6 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-center gap-2 mb-6">
            <img src={SEALS.asscompact2025} alt="Beste Schadenregulierung" className="h-10 opacity-70" />
            <span className="text-slate-500 text-xs">Testsieger – Beste Schadenregulierung</span>
          </motion.div>
        )}

        {/* Rating bar */}
        <div className="flex items-center justify-center gap-2 mb-6 text-xs text-slate-500">
          <div className="flex gap-0.5">{[1,2,3,4,5].map(i => <Star key={i} className={`w-3.5 h-3.5 fill-current ${i <= 4 ? 'text-lime-200' : 'text-lime-200/40'}`} />)}</div>
          <span>4.5 (987 Bewertungen)</span>
        </div>

        {/* Micro-Reward Toast */}
        <AnimatePresence>
          {microReward && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="mb-4 p-2.5 rounded-lg bg-lime-200/10 border border-lime-200/20 text-center">
              <p className="text-lime-200 text-xs font-medium flex items-center justify-center gap-1.5"><Check className="w-3.5 h-3.5" /> {microReward}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Expert Check Overlay */}
        <AnimatePresence>
          {showExpertCheck && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="mb-4 p-6 rounded-2xl bg-slate-800/80 border border-slate-700 text-center">
              <div className="w-10 h-10 mx-auto mb-3 border-2 border-lime-200 border-t-transparent rounded-full animate-spin" />
              <p className="text-white text-sm font-medium">Prüfe Verfügbarkeit des Insider-Tarifs{formData.club ? ` für ${formData.club}` : ''}...</p>
              <p className="text-slate-500 text-xs mt-1">78% der Spieler in deiner Liga haben diesen Check bereits bestanden.</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quiz Card */}
        <div className="glass-card rounded-2xl p-6 md:p-8">
          <AnimatePresence mode="wait">
                  {/* STEP 1: Personalization + Club */}
                  {step === 1 && (
                    <motion.div key="s1" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} className="flex-1" data-testid="checkout-step-1">
                      <h3 className="font-display text-xl font-bold text-white mb-1">Für wen ist der KICKBACK-Schutz?</h3>
                      <p className="text-slate-400 text-sm mb-1">Wähle aus und gib deinen Verein an.</p>
                      <p className="text-slate-600 text-xs mb-4">19% der Amateurfußballer wählen den Pro-Tarif.</p>
                      <div className="grid grid-cols-2 gap-3 mb-5">
                        {[
                          { id: 'self', label: 'Ich selbst', desc: 'Eigener Schutz als Spieler', icon: Shield },
                          { id: 'other', label: 'Jemand anderes', desc: 'Kind, Partner oder Teamkollege', icon: Trophy },
                        ].map((option) => {
                          const Icon = option.icon;
                          return (
                            <motion.button key={option.id} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}
                              onClick={() => setFormData({ ...formData, protectWho: option.id })}
                              className={`p-5 rounded-xl border-2 text-left transition-all ${formData.protectWho === option.id ? 'border-lime-200 bg-lime-200/10' : 'border-slate-700 hover:border-slate-500'}`}
                              data-testid={`protect-${option.id}`}
                            >
                              <Icon className={`w-7 h-7 mb-2 ${formData.protectWho === option.id ? 'text-lime-200' : 'text-slate-400'}`} />
                              <div className={`font-bold text-sm mb-0.5 ${formData.protectWho === option.id ? 'text-lime-200' : 'text-white'}`}>{option.label}</div>
                              <div className="text-slate-500 text-xs">{option.desc}</div>
                            </motion.button>
                          );
                        })}
                      </div>

                      {/* Club Search */}
                      <AnimatePresence>
                        {formData.protectWho && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="relative">
                            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="mb-3 p-2 rounded-lg bg-lime-200/5 border border-lime-200/10 text-center">
                                <p className="text-lime-200 text-xs font-medium">Glückwunsch! Du hast den Insider-Tarif erwischt, den die meisten wählen.</p>
                            </motion.div>
                            <Label className="text-slate-300 text-sm">Für welchen Verein spielst du?</Label>
                            <p className="text-slate-600 text-xs mb-2">Wir prüfen im Hintergrund, ob dein Club für den Amateur-Tarif (bis Regionalliga) berechtigt ist.</p>
                            {!formData.club ? (
                              <>
                                <Input
                                  value={formData.clubSearch}
                                  onChange={(e) => { setFormData({ ...formData, clubSearch: e.target.value, club: '' }); searchClubs(e.target.value); setClubManual(false); }}
                                  onFocus={() => formData.clubSearch.length >= 2 && setShowClubList(true)}
                                  className="bg-slate-800/50 border-slate-700 text-white"
                                  placeholder="z.B. SV Blau-Weiß Beelen..."
                                  data-testid="club-search"
                                />
                                <AnimatePresence>
                                  {showClubList && clubResults.length > 0 && !clubManual && (
                                    <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                                      className="absolute z-20 top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg max-h-40 overflow-y-auto shadow-xl">
                                      {clubResults.map((club) => (
                                        <button key={club} onClick={() => { setFormData({ ...formData, club, clubSearch: '' }); setShowClubList(false); }}
                                          className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-lime-200/10 hover:text-lime-200 transition-colors border-b border-slate-700/50 last:border-0"
                                        >{club}</button>
                                      ))}
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                                {formData.clubSearch.length >= 2 && clubResults.length === 0 && !clubManual && (
                                  <p className="text-slate-500 text-xs mt-1">Kein Ergebnis gefunden.</p>
                                )}
                                {!clubManual && formData.clubSearch.length >= 2 && (
                                  <button onClick={() => { setClubManual(true); setShowClubList(false); setFormData({ ...formData, club: formData.clubSearch }); }}
                                    className="text-lime-200/70 text-xs mt-1.5 hover:text-lime-200 underline" data-testid="club-manual-entry"
                                  >Verein nicht in der Liste? Hier manuell eingeben.</button>
                                )}
                              </>
                            ) : (
                              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-1 inline-flex items-center gap-2 px-3 py-1.5 bg-lime-200/10 border border-lime-200/30 rounded-full text-lime-200 text-sm">
                                <FootballIcon size={14} className="text-lime-200" /> {formData.club}
                                <button onClick={() => setFormData({ ...formData, club: '', clubSearch: '' })} className="ml-1 hover:text-white"><X className="w-3 h-3" /></button>
                              </motion.div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )}

                  {/* STEP 2: Birthdate */}
                  {step === 2 && (
                    <motion.div key="s2" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} className="flex-1" data-testid="checkout-step-2">
                      <h3 className="font-display text-xl font-bold text-white mb-1">Wann bist du geboren?</h3>
                      <p className="text-slate-400 text-sm mb-1">{formData.club ? `Dein Schutz für die Spiele beim ${formData.club} ist fast bereit.` : 'Dein Alter für die Tarifberechnung.'}</p>
                      <p className="text-slate-600 text-xs mb-4">92% der Spieler deiner Altersgruppe sind bereits abgesichert.</p>
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        {['Tag', 'Monat', 'Jahr'].map((label, i) => {
                          const items = i === 0 ? days : i === 1 ? months : years;
                          const key = i === 0 ? 'birthDay' : i === 1 ? 'birthMonth' : 'birthYear';
                          return (
                            <div key={label}>
                              <Label className="text-slate-400 text-xs">{label}</Label>
                              <div className="scroll-wheel mt-2 bg-slate-800/50 rounded-lg">
                                {items.map((item) => (
                                  <div key={item} className={`scroll-wheel-item cursor-pointer ${formData[key] === item ? 'selected text-lime-200' : 'text-slate-400'}`}
                                    onClick={() => {
                                      setFormData({ ...formData, [key]: item });
                                      if (key === 'birthYear') { setAgeOk(null); }
                                    }}
                                  >{item.toString().padStart(2, '0')}</div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Age Check */}
                      <div className="flex items-center gap-3">
                        <Button onClick={simulateAgeCheck} variant="outline" className="border-slate-600 text-slate-300 text-sm" disabled={ageChecking} data-testid="age-check-btn">
                          {ageChecking ? 'Prüfe...' : 'Alter prüfen'}
                        </Button>
                        <AnimatePresence>
                          {ageChecking && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-5 h-5 border-2 border-lime-200 border-t-transparent rounded-full animate-spin" />}
                          {ageOk === true && <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex items-center gap-1 text-lime-200 text-sm"><Check className="w-4 h-4" /> Passt!</motion.div>}
                          {ageOk === false && <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-amber-400 text-sm">Alter nicht im Rahmen (16–65)</motion.div>}
                        </AnimatePresence>
                      </div>
                      {/* Video after age check */}
                      <AnimatePresence>
                        {ageOk === true && (
                          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-4">
                            <video
                              src="https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/ql25h4ag_Fussballer%20Startseite.mp4"
                              className="w-full rounded-xl border border-slate-700"
                              autoPlay playsInline controls
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )}


                  {/* STEP 3: Package Selection */}
                  {step === 3 && (
                    <motion.div key="s3" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} className="flex-1" data-testid="checkout-step-3">
                      <h3 className="font-display text-xl font-bold text-white mb-1">Wähle deinen Schutz</h3>
                      <p className="text-slate-400 text-sm mb-1">{formData.club ? `Dein Schutz für ${formData.club}.` : 'Welches Paket passt zu dir?'}</p>
                      <p className="text-slate-600 text-xs mb-4">67% der Spieler wählen das Pro-Paket.</p>

                      <div className="space-y-3">
                        {PACKAGES.map((p, i) => {
                          const fullPrice = (p.price * 2);
                          const showDiscount = formData.couponActive;
                          return (
                            <motion.button key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                              onClick={() => setSelectedPkg(p.id)}
                              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                                selectedPackage === p.id ? 'border-lime-200 bg-lime-200/5' : 'border-slate-700 hover:border-slate-500'
                              }`}
                              data-testid={`quiz-pkg-${p.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPackage === p.id ? 'border-lime-200 bg-lime-200' : 'border-slate-600'}`}>
                                    {selectedPackage === p.id && <div className="w-2 h-2 rounded-full bg-slate-900" />}
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className={`font-bold ${selectedPackage === p.id ? 'text-lime-200' : 'text-white'}`}>{p.name}</span>
                                      {p.highlighted && <span className="text-[9px] px-1.5 py-0.5 bg-lime-200 text-slate-900 rounded-full font-bold">BELIEBT</span>}
                                    </div>
                                    <span className="text-slate-500 text-xs">{p.features[0]}</span>
                                  </div>
                                </div>
                                <div className="text-right">
                                  {showDiscount ? (
                                    <>
                                      <div className="text-slate-600 text-xs line-through">{fullPrice.toFixed(2).replace('.', ',')}€</div>
                                      <div className={`font-display text-lg font-bold ${selectedPackage === p.id ? 'text-lime-200' : 'text-white'}`}>{p.price.toFixed(2).replace('.', ',')}€</div>
                                      <div className="text-lime-200/60 text-[10px] font-medium">-50%</div>
                                    </>
                                  ) : (
                                    <>
                                      <div className={`font-display text-lg font-bold ${selectedPackage === p.id ? 'text-lime-200' : 'text-white'}`}>{fullPrice.toFixed(2).replace('.', ',')}€</div>
                                      <div className="text-slate-600 text-[10px]">/Monat</div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </motion.button>
                          );
                        })}
                      </div>

                      {/* Coupon Code Field */}
                      <div className="mt-4 p-3 rounded-xl bg-slate-800/50 border border-lime-200/30 relative overflow-hidden">
                        {!formData.couponActive && (
                          <div className="absolute inset-0 bg-lime-200/5 animate-pulse pointer-events-none" />
                        )}
                        {!formData.couponActive ? (
                          <div>
                            <p className="text-lime-200 text-xs font-medium mb-2 flex items-center gap-1"><Zap className="w-3 h-3" /> Du hast einen Rabattcode!</p>
                            <div className="flex gap-2">
                              <Input
                                value={formData.couponCode || 'KICKBACK50'}
                                readOnly
                                className="bg-slate-900/50 border-lime-200/30 text-lime-200 text-sm font-mono flex-1"
                                data-testid="coupon-input"
                              />
                              <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
                                <Button
                                  onClick={() => setFormData({ ...formData, couponActive: true, couponCode: 'KICKBACK50' })}
                                  className="bg-lime-200 hover:bg-lime-300 text-slate-900 font-bold text-sm px-5 shadow-glow-lime animate-pulse-glow"
                                  data-testid="coupon-activate"
                                >
                                  -50% Einlösen
                                </Button>
                              </motion.div>
                            </div>
                            <p className="text-slate-500 text-[10px] mt-1.5">Klicke auf Einlösen, um deinen 50% Rabatt zu aktivieren.</p>
                          </div>
                        ) : (
                          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-lime-200 flex items-center justify-center flex-shrink-0">
                              <Check className="w-4 h-4 text-slate-900" />
                            </div>
                            <div>
                              <p className="text-lime-200 text-sm font-bold">KICKBACK50 – 50% Rabatt aktiviert!</p>
                              <p className="text-slate-500 text-[10px]">Dauerhafter Rabatt auf deinen gewählten Tarif.</p>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  )}
                  {/* STEP 4: Risk - Job & Health */}
                  {step === 4 && (
                    <motion.div key="s4" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} className="flex-1" data-testid="checkout-step-4">
                      <h3 className="font-display text-xl font-bold text-white mb-1">Kurzer Risikocheck</h3>
                      <p className="text-slate-400 text-sm mb-1">Zwei schnelle Fragen – dann geht's weiter.</p>
                      <p className="text-slate-600 text-xs mb-4">78% der Spieler in deiner Liga haben diesen Punkt bereits bestätigt.</p>

                      {/* Job Autocomplete */}
                      <div className="mb-6 relative">
                        <Label className="text-slate-300 text-sm">Beruf</Label>
                        <Input
                          value={formData.jobSearch || formData.job}
                          onChange={(e) => {
                            setFormData({ ...formData, jobSearch: e.target.value, job: '' });
                            setShowJobList(true);
                          }}
                          onFocus={() => setShowJobList(true)}
                          className="mt-1 bg-slate-800/50 border-slate-700 text-white"
                          placeholder="Beruf eingeben..."
                          data-testid="job-search"
                        />
                        <AnimatePresence>
                          {showJobList && formData.job === '' && (
                            <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                              className="absolute z-20 top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg max-h-40 overflow-y-auto shadow-xl">
                              {filteredJobs.map((job) => (
                                <button key={job} onClick={() => { setFormData({ ...formData, job, jobSearch: '' }); setShowJobList(false); }}
                                  className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-lime-200/10 hover:text-lime-200 transition-colors"
                                  data-testid={`job-option-${job}`}
                                >{job}</button>
                              ))}
                              {filteredJobs.length === 0 && <div className="px-4 py-2 text-sm text-slate-500">Kein Ergebnis</div>}
                            </motion.div>
                          )}
                        </AnimatePresence>
                        {formData.job && (
                          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-lime-200/10 border border-lime-200/30 rounded-full text-lime-200 text-sm">
                            <Check className="w-3 h-3" /> {formData.job}
                            <button onClick={() => setFormData({ ...formData, job: '', jobSearch: '', customJob: '' })} className="ml-1 hover:text-white"><X className="w-3 h-3" /></button>
                          </motion.div>
                        )}
                        {/* Custom job input for "Sonstige" */}
                        <AnimatePresence>
                          {formData.job === 'Sonstige' && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-3">
                              <Label className="text-slate-400 text-xs">Bitte Beruf angeben</Label>
                              <Input
                                value={formData.customJob}
                                onChange={(e) => setFormData({ ...formData, customJob: e.target.value })}
                                className="mt-1 bg-slate-800/50 border-slate-700 text-white"
                                placeholder="z.B. Physiotherapeut, Freelancer..."
                                autoFocus
                                data-testid="custom-job-input"
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Health Toggle */}
                      <div className="p-5 rounded-xl bg-slate-800/50 border border-slate-700">
                        <p className="text-white text-sm mb-4">Wurde in den letzten 5 Jahren ein Sportunfall bei einem Vorversicherer gemeldet?</p>
                        <div className="flex items-center gap-4">
                          <button onClick={() => setFormData({ ...formData, healthCheck: false })}
                            className={`px-5 py-2 rounded-full border-2 text-sm transition-all ${formData.healthCheck === false ? 'border-lime-200 bg-lime-200/10 text-lime-200 font-bold' : 'border-slate-600 text-slate-400 hover:border-slate-500'}`}
                            data-testid="health-no">Nein</button>
                          <button onClick={() => setFormData({ ...formData, healthCheck: true })}
                            className={`px-5 py-2 rounded-full border-2 text-sm transition-all ${formData.healthCheck === true ? 'border-amber-500 bg-amber-500/10 text-amber-400 font-bold' : 'border-slate-600 text-slate-400 hover:border-slate-500'}`}
                            data-testid="health-yes">Ja</button>
                        </div>
                        <AnimatePresence>
                          {formData.healthCheck === false && (
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-3 flex items-center gap-2 text-lime-200 text-sm">
                              <Check className="w-4 h-4" /> Perfekt, keine Vorschäden!
                            </motion.div>
                          )}
                          {formData.healthCheck === true && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                              className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                              <p className="text-amber-300 text-sm">Kein Problem – individuelle Prüfung notwendig. Unser Team meldet sich innerhalb von 24h bei dir.</p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  )}

                  {/* STEP 5: Addon Upgrade */}
                  {step === 5 && (
                    <motion.div key="s5" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} className="flex-1" data-testid="checkout-step-5">
                      <h3 className="font-display text-xl font-bold text-white mb-2">Wie ein Profi zurückkommen?</h3>
                      <p className="text-slate-400 text-sm mb-6">Optionales Reha-Upgrade für deine Genesung.</p>

                      <motion.button
                        onClick={() => setFormData({ ...formData, addon: !formData.addon })}
                        className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
                          formData.addon
                            ? 'border-lime-200 bg-lime-200/5'
                            : 'border-slate-700 hover:border-slate-500'
                        }`}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        data-testid="addon-toggle"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                formData.addon ? 'bg-lime-200 border-lime-200' : 'border-slate-600'
                              }`}>
                                {formData.addon && <Check className="w-3 h-3 text-slate-900" />}
                              </div>
                              <span className="font-bold text-white">Profi-Reha Upgrade</span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-lime-200/20 text-lime-200 font-medium">Beliebt</span>
                            </div>
                            <div className="ml-7 space-y-1.5">
                              <div className="flex items-center gap-2 text-sm">
                                <Check className="w-3.5 h-3.5 text-lime-200 flex-shrink-0" />
                                <span className="text-slate-300">Ambulante Reha wie ein <strong className="text-white">Profi-Spieler</strong></span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Check className="w-3.5 h-3.5 text-lime-200 flex-shrink-0" />
                                <span className="text-slate-300">Bis zu <strong className="text-white">50€/Tag</strong> für Experten-Behandlung</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Check className="w-3.5 h-3.5 text-lime-200 flex-shrink-0" />
                                <span className="text-slate-300">Max. Reha-Budget <strong className="text-white">2.500€</strong></span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="font-display text-2xl font-bold text-lime-200">+1,99€</div>
                            <div className="text-xs text-slate-500">/Monat</div>
                          </div>
                        </div>
                      </motion.button>

                      {formData.addon && (
                        <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="mt-3 p-3 rounded-lg bg-lime-200/5 border border-lime-200/20 text-center">
                          <p className="text-sm text-slate-300">Neuer Gesamtbeitrag: <span className="font-display text-lg font-bold text-lime-200">{(pkg?.price + 5).toFixed(2).replace('.', ',')}€</span> /Monat</p>
                        </motion.div>
                      )}

                      {!formData.addon && (
                        <p className="text-xs text-slate-600 mt-3 text-center">Du kannst das Addon auch später hinzufügen.</p>
                      )}
                    </motion.div>
                  )}

                  {/* STEP 6: Personal Data + IBAN */}
                  {step === 6 && (
                    <motion.div key="s6" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} className="flex-1" data-testid="checkout-step-6">
                      {/* Confirmation Header */}
                      <div className="text-center mb-4">
                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-12 h-12 mx-auto mb-2 rounded-full bg-lime-200/10 border border-lime-200/30 flex items-center justify-center">
                          <Check className="w-6 h-6 text-lime-200" />
                        </motion.div>
                        <h3 className="font-display text-lg font-bold text-white mb-1">Bestätige jetzt deinen Schutz zum Aktionspreis.</h3>
                        <p className="text-slate-400 text-xs">{formData.firstName ? `${formData.firstName}, d` : 'D'}ein Zertifikat wird nach Abschluss sofort per E-Mail versendet.</p>
                      </div>

                      {/* Price Breakdown */}
                      <div className="p-4 rounded-xl bg-slate-800/50 border border-lime-200/20 mb-3">
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span className="text-slate-400">Gewähltes Paket:</span><span className="text-white font-medium">{pkg?.name} ({pkg?.payout?.toLocaleString('de-DE')}€ Sofort-Cash)</span></div>
                          <div className="flex justify-between"><span className="text-slate-400">Regulärer Preis:</span><span className="text-slate-500 line-through">{(pkg?.price * 2).toFixed(2).replace('.', ',')}€ / Monat</span></div>
                          <div className="flex justify-between"><span className="text-lime-200 font-medium">Aktions-Vorteil (KICKBACK50):</span><span className="text-lime-200 font-bold">-{pkg?.price.toFixed(2).replace('.', ',')}€</span></div>
                          <div className="border-t border-slate-700 pt-2 flex justify-between"><span className="text-white font-bold">Monatsbeitrag:</span><span className="text-lime-200 font-display text-xl font-bold">{pkg?.price.toFixed(2).replace('.', ',')}€</span></div>
                        </div>
                      </div>

                      {/* Urgency */}
                      <div className="flex items-center justify-center gap-2 mb-4 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                        <p className="text-amber-300 text-xs">Nur noch heute: <strong>124 andere Kicker</strong> haben ihren Rabatt-Code heute eingelöst.</p>
                      </div>

                      {/* Personal Data */}
                      <div className="space-y-3 mb-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-slate-300 text-xs">Vorname</Label>
                            <Input value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                              className="mt-1 bg-slate-800/50 border-slate-700 text-white text-sm" placeholder="Max" data-testid="input-firstname" />
                          </div>
                          <div>
                            <Label className="text-slate-300 text-xs">Nachname</Label>
                            <Input value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                              className="mt-1 bg-slate-800/50 border-slate-700 text-white text-sm" placeholder="Mustermann" data-testid="input-lastname" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Straße & Hausnummer</Label>
                          <Input value={formData.street} onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                            className="mt-1 bg-slate-800/50 border-slate-700 text-white text-sm" placeholder="Musterstr. 12" data-testid="input-street" />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-slate-300 text-xs">PLZ</Label>
                            <Input value={formData.zip} onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                              className="mt-1 bg-slate-800/50 border-slate-700 text-white text-sm" placeholder="53797" data-testid="input-zip" />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-slate-300 text-xs">Ort</Label>
                            <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                              className="mt-1 bg-slate-800/50 border-slate-700 text-white text-sm" placeholder="Lohmar" data-testid="input-city" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-slate-300 text-xs">E-Mail</Label>
                            <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                              className="mt-1 bg-slate-800/50 border-slate-700 text-white text-sm" placeholder="max@beispiel.de" data-testid="input-email" />
                          </div>
                          <div>
                            <Label className="text-slate-300 text-xs">Telefon</Label>
                            <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                              className="mt-1 bg-slate-800/50 border-slate-700 text-white text-sm" placeholder="0177 1234567" data-testid="input-phone" />
                          </div>
                        </div>

                        <div className="flex items-center gap-2 px-1 text-xs text-slate-500">
                          <FootballIcon size={12} className="text-lime-200/50 flex-shrink-0" />
                          <span>Gültig für alle Spieler (Startelf &amp; Auswechselbank) bis zur Regionalliga.</span>
                        </div>

                        {/* Policyholder checkbox */}
                        <div className="p-3 rounded-xl bg-slate-800/50 border border-slate-700">
                          <button onClick={() => setFormData({ ...formData, isAlsoPolicyholder: !formData.isAlsoPolicyholder })}
                            className="flex items-start gap-3 w-full text-left" data-testid="policyholder-toggle">
                            <div className={`mt-0.5 w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all ${formData.isAlsoPolicyholder ? 'bg-lime-200 border-lime-200' : 'border-slate-600'}`}>
                              {formData.isAlsoPolicyholder && <Check className="w-3 h-3 text-slate-900" />}
                            </div>
                            <span className="text-slate-300 text-xs">Die versicherte Person ist gleichzeitig der Versicherungsnehmer.</span>
                          </button>
                          <AnimatePresence>
                            {!formData.isAlsoPolicyholder && (
                              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-3 space-y-2 pt-3 border-t border-slate-700/50">
                                <p className="text-slate-500 text-xs">Angaben zum Versicherungsnehmer:</p>
                                <Input value={formData.policyholderName} onChange={(e) => setFormData({ ...formData, policyholderName: e.target.value })}
                                  className="bg-slate-800/50 border-slate-700 text-white text-sm" placeholder="Vor- und Nachname des VN" data-testid="input-ph-name" />
                                <Input value={formData.policyholderAddress} onChange={(e) => setFormData({ ...formData, policyholderAddress: e.target.value })}
                                  className="bg-slate-800/50 border-slate-700 text-white text-sm" placeholder="Adresse des VN (Straße, PLZ Ort)" data-testid="input-ph-address" />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>

                      {/* IBAN Section */}
                      <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50 mb-4">
                        <h4 className="text-white text-sm font-bold mb-1">Konto für die {pkg?.payout?.toLocaleString('de-DE')}€ Sofort-Auszahlung (IBAN)</h4>
                        <p className="text-slate-500 text-[10px] mb-3">Wir nutzen dieses Konto auch für den Einzug deines Beitrags per Lastschrift. Die erste Abbuchung erfolgt erst bei offiziellem Schutzbeginn.</p>

                        {/* Payment Interval */}
                        <div className="grid grid-cols-3 gap-2 mb-3">
                          {[
                            { id: 'monthly', label: 'Monatlich', months: 1, factor: 1, sub: 'Flexibel', badge: null },
                            { id: 'halfyearly', label: 'Halbjährlich', months: 6, factor: 0.95, sub: '5% sparen', badge: null },
                            { id: 'yearly', label: 'Jährlich', months: 10, factor: null, sub: '2 Monate gratis', badge: 'BESTER DEAL' },
                          ].map((opt) => {
                            const base = (pkg?.price || 0) + (formData.addon ? 1.99 : 0);
                            const total = opt.factor !== null ? base * opt.months * opt.factor : base * opt.months;
                            const isActive = formData.paymentInterval === opt.id;
                            return (
                              <button key={opt.id} onClick={() => setFormData({ ...formData, paymentInterval: opt.id })}
                                className={`relative p-2 rounded-lg border-2 text-center transition-all ${isActive ? 'border-lime-200 bg-lime-200/5' : 'border-slate-700 hover:border-slate-500'}`}>
                                {opt.badge && <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-lime-200 text-slate-900 text-[8px] font-bold rounded-full whitespace-nowrap">{opt.badge}</div>}
                                <div className={`text-[10px] font-medium mb-0.5 ${isActive ? 'text-lime-200' : 'text-slate-400'}`}>{opt.label}</div>
                                <div className={`font-display text-sm font-bold ${isActive ? 'text-white' : 'text-slate-500'}`}>{total.toFixed(2).replace('.', ',')}€</div>
                                <div className={`text-[9px] ${isActive ? 'text-lime-200/70' : 'text-slate-600'}`}>{opt.sub}</div>
                              </button>
                            );
                          })}
                        </div>

                        <div className="relative">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-slate-300 text-xs font-medium">IBAN</span>
                            {detectedBank && (
                              <motion.span initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} className="ml-auto text-[10px] text-slate-400 bg-slate-700/80 px-2 py-0.5 rounded-full">
                                {detectedBank}
                              </motion.span>
                            )}
                          </div>
                          <div className="h-1 bg-slate-700 rounded-full overflow-hidden mb-2">
                            <motion.div className="h-full bg-lime-200" animate={{ width: `${ibanProgress}%` }} transition={{ duration: 0.2 }} />
                          </div>
                          <Input
                            value={formData.iban}
                            onChange={handleIbanChange}
                            className={`bg-slate-900/50 border-slate-600 text-white font-mono tracking-wider ${
                              ibanValid === true ? 'border-lime-200 shadow-[0_0_10px_rgba(217,249,157,0.2)]' : ''
                            } ${ibanValid === false ? 'border-red-500' : ''}`}
                            placeholder="DE89 3704 0044 0532 0130 00"
                            data-testid="input-iban"
                          />
                          {ibanValid === true && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-1 text-lime-200 text-xs flex items-center gap-1"><Check className="w-3 h-3" /> IBAN verifiziert</motion.p>}
                          {ibanValid === false && <p className="text-red-400 text-xs mt-1">Ungültige IBAN.</p>}
                        </div>
                      </div>

                      {/* Legal Checkboxes - 2-Click System */}
                      <div className="mt-5 p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                        {/* Accept All Button */}
                        {(!legal1 || !legal2) && (
                          <button
                            onClick={() => { setLegal1(true); setLegal2(true); }}
                            className="w-full mb-4 py-2.5 rounded-lg bg-lime-200/10 border border-lime-200/30 text-lime-200 text-xs font-medium hover:bg-lime-200/20 transition-all"
                            data-testid="accept-all-btn"
                          >
                            Alles mit einem Klick akzeptieren
                          </button>
                        )}

                        {/* Checkbox 1: Vertrag & Dokumente */}
                        <button onClick={() => setLegal1(!legal1)}
                          className="flex items-start gap-3 w-full text-left mb-3" data-testid="legal-box-1">
                          <div className={`mt-0.5 w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all ${legal1 ? 'bg-lime-200 border-lime-200' : 'border-slate-500 hover:border-lime-200/50'}`}>
                            {legal1 && <Check className="w-3 h-3 text-slate-900" />}
                          </div>
                          <span className={`text-xs leading-relaxed ${legal1 ? 'text-slate-300' : 'text-slate-500'}`}>
                            Ich bestätige den Erhalt der <a href="https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/vb7tkdn1_KM-allsafe-bodyguard-2-0-Produktuebersicht_05-2019_1-06_komplett.pdf" target="_blank" rel="noopener noreferrer" className="text-lime-200 underline" onClick={(e) => e.stopPropagation()}>Produktinformationen</a>, <a href="https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/3b2rmwhx_KM-allsafe-bodyguard-2_0-U-Verbraucherinformationen_05-2019_1-05.pdf" target="_blank" rel="noopener noreferrer" className="text-lime-200 underline" onClick={(e) => e.stopPropagation()}>Erstinformationen</a> sowie der <a href="https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/6znd0f25_KM-allsafe-bodyguard-2-0-U-Annahmerichtlinien_05-2019_1-06.pdf" target="_blank" rel="noopener noreferrer" className="text-lime-200 underline" onClick={(e) => e.stopPropagation()}>Antragshinweise</a>, den sofortigen Versicherungsbeginn und die Bevollmächtigung von KICKBACK. Ich verzichte auf persönliche Beratung.
                          </span>
                        </button>

                        {/* Checkbox 2: Datenschutz & Gesundheit */}
                        <button onClick={() => setLegal2(!legal2)}
                          className="flex items-start gap-3 w-full text-left" data-testid="legal-box-2">
                          <div className={`mt-0.5 w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all ${legal2 ? 'bg-lime-200 border-lime-200' : 'border-slate-500 hover:border-lime-200/50'}`}>
                            {legal2 && <Check className="w-3 h-3 text-slate-900" />}
                          </div>
                          <span className={`text-xs leading-relaxed ${legal2 ? 'text-slate-300' : 'text-slate-500'}`}>
                            Ich stimme der <button onClick={(e) => { e.stopPropagation(); setCurrentPage && setCurrentPage('datenschutz'); }} className="text-lime-200 underline">Datenverarbeitung</button> (inkl. Gesundheitsdaten & Konzept & Marketing GmbH) sowie der Bonitätsprüfung zu.
                          </span>
                        </button>

                        {legal1 && legal2 && (
                          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-3 pt-3 border-t border-slate-700/50 flex items-center gap-2 text-lime-200 text-xs">
                            <Check className="w-4 h-4" /> Alle Bedingungen akzeptiert
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Navigation */}
                <div className="mt-6 pt-4 border-t border-slate-700/50">
                  {/* Last Look Summary - only on final step */}
                  {step === totalSteps && legalAccepted && formData.firstName && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      className="mb-4 p-4 rounded-xl bg-slate-800/50 border border-lime-200/20">
                      <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Deine Zusammenfassung</div>
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div>
                          <div className="text-white font-bold text-sm">{formData.firstName} {formData.lastName}</div>
                          <div className="text-slate-500 text-xs">{formData.club || 'Spieler'}</div>
                        </div>
                        <div>
                          <div className="text-lime-200 font-display text-lg font-bold">{pkg?.payout?.toLocaleString('de-DE')}€</div>
                          <div className="text-slate-500 text-xs">Schutz</div>
                        </div>
                        <div>
                          <div className="text-white font-bold text-sm">{(() => {
                            const base = (pkg?.price || 0) + (formData.addon ? 1.99 : 0);
                            if (formData.paymentInterval === 'yearly') return (base * 10).toFixed(2).replace('.', ',');
                            if (formData.paymentInterval === 'halfyearly') return (base * 6 * 0.95).toFixed(2).replace('.', ',');
                            return base.toFixed(2).replace('.', ',');
                          })()}€</div>
                          <div className="text-slate-500 text-xs">{formData.paymentInterval === 'yearly' ? '/Jahr' : formData.paymentInterval === 'halfyearly' ? '/Halbjahr' : '/Monat'}</div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-700/50 text-center">
                        <div className="text-slate-400 text-xs">Versicherungsbeginn: <span className="text-white font-medium">{(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }); })()}</span></div>
                      </div>
                    </motion.div>
                  )}

                  <div className="flex gap-3">
                    {step > 1 && (
                      <Button onClick={prevStep} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="btn-back">Zurück</Button>
                    )}
                    <div className="flex-1" />
                    {step < totalSteps ? (
                      <Button onClick={nextStep} disabled={!canProceed()}
                        className="shimmer-btn bg-lime-200 hover:bg-lime-300 text-slate-900 font-bold px-8 disabled:opacity-40" data-testid="btn-next">
                        {step === 1 ? 'Vorteil sichern' : step === 3 ? 'Paket bestätigen' : step === 4 ? 'Daten bestätigen & weiter' : step === 5 ? 'Schritt abschließen' : 'Weiter'} <ChevronRight className="ml-1 w-4 h-4" />
                      </Button>
                    ) : (
                      <motion.div whileHover={{ scale: canProceed() ? 1.03 : 1 }} whileTap={{ scale: canProceed() ? 0.97 : 1 }}>
                        <Button onClick={handleSubmit} disabled={!canProceed() || isSubmitting}
                          className={`font-bold px-8 transition-all duration-500 ${
                            canProceed()
                              ? 'bg-lime-200 hover:bg-lime-300 text-slate-900 shadow-glow-lime animate-pulse-glow'
                              : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                          }`}
                          data-testid="btn-submit">
                          {isSubmitting ? (
                            <div className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /> {submitPhase === 'validating' ? 'Antrag wird validiert...' : submitPhase === 'activating' ? 'Schutz wird aktiviert...' : 'Fertig!'}</div>
                          ) : `JETZT SCHUTZ FÜR ${pkg?.price.toFixed(2).replace('.', ',')}€ AKTIVIEREN`}
                        </Button>
                      </motion.div>
                    )}
                  </div>

                  {/* Trust under submit */}
                  {step === totalSteps && (
                    <div className="flex flex-wrap justify-center gap-4 mt-4 text-[11px] text-slate-500">
                      <span className="flex items-center gap-1"><Shield className="w-3 h-3 text-emerald-400" /> SSL-verschlüsselt</span>
                      <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-400" /> Monatlich kündbar nach 12 Mon.</span>
                      <span className="flex items-center gap-1"><BadgeCheck className="w-3 h-3 text-emerald-400" /> 14 Tage Widerrufsrecht</span>
                    </div>
                  )}
                </div>

                {/* Quiz footer trust */}
                <div className="mt-4 text-center">
                  <p className="text-slate-600 text-[10px]">Du kannst den Antrag jederzeit abbrechen. Kein Risiko.</p>
                  <SocialProofTicker />
                </div>
              </div>

              {/* Compact package info */}
              {pkg && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 p-3 glass-card rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FootballIcon size={16} className="text-lime-200" />
                    <span className="text-white text-sm font-medium">{pkg.name}</span>
                    {formData.club && <span className="text-slate-500 text-xs">· {formData.club}</span>}
                  </div>
                  <div className="text-right">
                    <span className="text-lime-200 font-display font-bold">{(() => {
                      const base = (pkg?.price || 0) + (formData.addon ? 1.99 : 0);
                      if (formData.paymentInterval === 'yearly') return (base * 10).toFixed(2).replace('.', ',') + '€/Jahr';
                      if (formData.paymentInterval === 'halfyearly') return (base * 6 * 0.95).toFixed(2).replace('.', ',') + '€/Halbj.';
                      return base.toFixed(2).replace('.', ',') + '€/mtl.';
                    })()}</span>
                  </div>
                </motion.div>
              )}
        </div>
      </div>
  );
};

// Success Animation Component
const SuccessAnimation = ({ onClose, userData }) => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() + 1);
  const dateStr = startDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const memberId = `KB-${Date.now().toString(36).toUpperCase().slice(-6)}`;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="success-overlay" data-testid="success-overlay">
      <div className="text-center px-4 max-w-lg" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Football celebration */}
        <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="w-24 h-24 mx-auto mb-5 rounded-full border-4 border-lime-200 flex items-center justify-center shadow-glow-lime-lg bg-slate-800/50">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.6, type: 'spring' }}>
            <FootballIcon size={40} className="text-lime-200" />
          </motion.div>
        </motion.div>

        <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="font-display text-3xl md:text-4xl font-bold text-white mb-2 italic">
          WILLKOMMEN IM TEAM!
        </motion.h2>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          className="text-slate-300 mb-1">
          Dein Schutz ist ab <span className="text-lime-200 font-bold">{dateStr}</span> aktiv.
        </motion.p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
          className="text-slate-500 text-sm mb-5">Du erhältst eine Bestätigung per E-Mail.</motion.p>

        {/* Digital Member Card / Kader-Ausweis */}
        <motion.div initial={{ opacity: 0, y: 30, rotateX: 20 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ delay: 0.8, duration: 0.6 }}
          className="mx-auto max-w-xs mb-5" data-testid="member-card">
          <div className="rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0F172A 50%, #1a2332 100%)', border: '1px solid rgba(217,249,157,0.2)' }}>
            {/* Card Header */}
            <div className="px-5 pt-4 pb-3 flex items-center justify-between">
              <img src="/kickback-logo.png" alt="KICKBACK" className="h-6 w-auto opacity-90" />
              <span className="text-lime-200 text-[10px] font-bold uppercase tracking-widest">Aktiver Schutz</span>
            </div>
            {/* Card Body */}
            <div className="px-5 pb-4">
              <div className="text-white font-display text-xl font-bold">{userData?.firstName || 'Max'} {userData?.lastName || 'Muster'}</div>
              <div className="text-slate-400 text-sm mt-0.5">{userData?.club || 'Amateurfußballer'}</div>
              <div className="mt-4 flex items-end justify-between">
                <div>
                  <div className="text-[10px] text-slate-500 uppercase">Deckung</div>
                  <div className="text-lime-200 font-display text-2xl font-bold">{(userData?.payout || 3000).toLocaleString('de-DE')}€</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-slate-500 uppercase">Paket</div>
                  <div className="text-white font-bold">{userData?.packageName || 'Pro'}</div>
                </div>
              </div>
            </div>
            {/* Card Footer */}
            <div className="px-5 py-2.5 flex items-center justify-between" style={{ background: 'rgba(217,249,157,0.08)', borderTop: '1px solid rgba(217,249,157,0.1)' }}>
              <span className="text-slate-500 text-[10px] font-mono">{memberId}</span>
              <span className="text-slate-500 text-[10px]">Gültig ab {dateStr}</span>
            </div>
          </div>
        </motion.div>

        {/* Lisa personal card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }}
          className="glass-card rounded-2xl p-4 mb-5 text-left max-w-xs mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-lime-200/40 flex-shrink-0">
              <img src="https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/rhm1bkbk_unnamed%20-%202026-02-21T105249.292.jpg" alt="Lisa" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-white text-xs font-medium">Lisa vom KICKBACK-Team</p>
              <p className="text-slate-400 text-[11px]">Dein Schutzschild ist aktiv. Bei Fragen schreib mir!</p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }} className="flex flex-col gap-2 items-center max-w-xs mx-auto">
          <Button onClick={onClose} className="w-full shimmer-btn bg-lime-200 hover:bg-lime-300 text-slate-900 font-bold py-3 rounded-full" data-testid="success-close-btn">
            Alles klar
          </Button>
          <a href="https://k-m.info/schadenformular-unfall/" target="_blank" rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full border border-slate-700 text-slate-400 text-sm hover:border-red-500/30 hover:text-red-400 transition-all" data-testid="claim-button">
            <Activity className="w-4 h-4" /> Schadensfall melden
          </a>
        </motion.div>
        <p className="text-slate-600 text-[10px] mt-3">Tipp: Speichere diese Seite auf deinem Homescreen.</p>

        {/* Confetti */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <motion.div key={i} className="confetti-piece"
              style={{ left: `${Math.random() * 100}%`, backgroundColor: ['#D9F99D', '#BEF264', '#84CC16', '#F8FAFC', '#FACC15'][Math.floor(Math.random() * 5)], width: `${8 + Math.random() * 8}px`, height: `${8 + Math.random() * 8}px` }}
              initial={{ y: -20, opacity: 1, rotate: 0 }}
              animate={{ y: '100vh', opacity: 0, rotate: Math.random() * 720 - 360 }}
              transition={{ duration: 2 + Math.random() * 2, delay: 0.3 + Math.random() * 0.5, ease: 'easeOut' }}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Sticky CTA Component
const StickyCTA = ({ visible, onClick }) => {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          exit={{ y: 100 }}
          className="fixed bottom-0 left-0 right-0 z-40 p-4 bg-gradient-to-t from-slate-900 via-slate-900/95 to-transparent md:hidden"
          data-testid="sticky-cta"
        >
          <Button
            onClick={onClick}
            className="w-full bg-lime-200 hover:bg-lime-300 text-slate-900 font-bold py-4 rounded-full shadow-glow-lime"
            data-testid="sticky-cta-btn"
          >
            Verfügbarkeit prüfen
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Trust Section / Footer
const TrustFooter = ({ setCurrentPage }) => {
  return (
    <footer className="py-16 border-t border-emerald-900/30 relative" data-testid="footer">
      {/* Subtle grass line at top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />
      <div className="container-main px-4">
        {/* Siegel / Certification Badges */}
        <div className="mb-12">
          <p className="text-center text-slate-500 text-sm mb-6">Zertifiziert & Gepr&uuml;ft</p>
          <div className="flex flex-wrap justify-center items-center gap-6">
            <div
              className="h-28 rounded-lg overflow-hidden hover:scale-105 transition-transform cursor-pointer"
              data-testid="seal-focus-money"
            >
              <img src="https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/h2m0hol1_Download%20%2857%29.png" alt="Focus Money - Ausgezeichnete Leistungen" className="h-full w-auto object-contain" />
            </div>
            <div
              className="h-28 rounded-lg overflow-hidden hover:scale-105 transition-transform cursor-pointer"
              data-testid="seal-ascore"
            >
              <img src="https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/bejw5xdw_Download%20%2856%29.png" alt="ASCORE Herausragend - Konzept & Marketing GmbH" className="h-full w-auto object-contain" />
            </div>
            <div
              className="h-28 rounded-lg overflow-hidden hover:scale-105 transition-transform cursor-pointer"
              data-testid="seal-asscompact-2025"
            >
              <img src="https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/347gm1ec_Download%20%2823%29.jpg" alt="AssCompact Award 7/2025 - Beste Schadenregulierung" className="h-full w-auto object-contain" />
            </div>
            <div
              className="h-28 rounded-lg overflow-hidden hover:scale-105 transition-transform cursor-pointer"
              data-testid="seal-asscompact-2020"
            >
              <img src="https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/i3byrpzf_Download.jpg" alt="AssCompact Award 2020 - Platz 1" className="h-full w-auto object-contain" />
            </div>
          </div>
        </div>
        
        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center items-center gap-8 mb-12">
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50">
            <BadgeCheck className="w-5 h-5 text-lime-200" />
            <span className="text-slate-300 text-sm">Made in Germany</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50">
            <Shield className="w-5 h-5 text-lime-200" />
            <span className="text-slate-300 text-sm">SSL Verschlüsselt</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50">
            <Globe className="w-5 h-5 text-lime-200" />
            <span className="text-slate-300 text-sm">DSGVO Konform</span>
          </div>
        </div>

        {/* Insurance Partners */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="glass-card rounded-2xl p-6 text-center">
            <p className="text-slate-500 text-xs uppercase tracking-widest mb-3">Entwickelt &amp; gebündelt von</p>
            <p className="text-white font-medium mb-4">Drei führende Versicherungsgesellschaften. Ein exklusiver Schutz.</p>
            <div className="flex flex-wrap justify-center items-center gap-6 mb-4">
              <span className="text-slate-300 font-display text-lg font-bold">Allianz</span>
              <span className="text-slate-600">|</span>
              <span className="text-slate-300 font-display text-lg font-bold">Gothaer</span>
              <span className="text-slate-600">|</span>
              <span className="text-slate-300 font-display text-lg font-bold">Württembergische</span>
            </div>
            <p className="text-slate-500 text-xs">Gebündelt in der <span className="text-slate-400">Konzept &amp; Marketing GmbH, Hannover</span> – exklusiv als <span className="text-lime-200">KICKBACK Fußballer-Versicherung</span>.</p>
          </div>
        </div>
        
        {/* Payment Methods */}
        <div className="flex justify-center items-center gap-6 mb-12">
          <div className="flex items-center gap-4 text-slate-500">
            <CreditCard className="w-8 h-8" />
            <Wallet className="w-8 h-8" />
            <span className="text-sm">SEPA Lastschrift</span>
          </div>
        </div>
        
        {/* Main Footer Content */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-left">
            <div className="flex items-center gap-2">
              <img src="/kickback-logo.png" alt="KICKBACK" className="h-10 w-auto" />
            </div>
            <p className="text-slate-500 text-sm mt-1">Die Fussball-Versicherung</p>
          </div>
          
          <div className="flex items-center gap-4 text-slate-500 text-sm">
            <span>Risikoträger: Allianz · Gothaer · Württembergische (via K&M GmbH)</span>
          </div>
          
          <div className="flex flex-wrap gap-4 md:gap-6 text-slate-500 text-sm">
            <button onClick={() => setCurrentPage('impressum')} className="hover:text-white transition-colors">Impressum</button>
            <button onClick={() => setCurrentPage('datenschutz')} className="hover:text-white transition-colors">Datenschutz</button>
            <a href="https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/3b2rmwhx_KM-allsafe-bodyguard-2_0-U-Verbraucherinformationen_05-2019_1-05.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Verbraucherinformationen</a>
            <a href="https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/vb7tkdn1_KM-allsafe-bodyguard-2-0-Produktuebersicht_05-2019_1-06_komplett.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Produktübersicht</a>
            <a href="https://customer-assets.emergentagent.com/job_2b82e33f-bdf0-4b9d-bca9-c432e89960d3/artifacts/6znd0f25_KM-allsafe-bodyguard-2-0-U-Annahmerichtlinien_05-2019_1-06.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Annahmerichtlinien</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Cookie Banner Component
const CookieBanner = ({ onAccept }) => {
  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-slate-800 border-t border-slate-700"
      data-testid="cookie-banner"
    >
      <div className="container-main px-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-300 text-sm text-center md:text-left">
            Diese Website verwendet Cookies, um die bestmögliche Funktionalität zu gewährleisten. 
            Durch die Nutzung stimmen Sie der Verwendung zu.
          </p>
          <div className="flex gap-3">
            <Button
              onClick={onAccept}
              className="bg-lime-200 hover:bg-lime-300 text-slate-900 font-bold px-6 py-2 rounded-full text-sm"
              data-testid="cookie-accept-btn"
            >
              Akzeptieren
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Impressum Page Component
const ImpressumPage = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-slate-900 py-20" data-testid="impressum-page">
      <div className="container-main px-4 max-w-3xl mx-auto">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-lime-200 mb-8 hover:underline"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Zurück zur Startseite
        </button>
        
        <h1 className="font-display text-4xl font-bold text-white mb-8">Impressum</h1>
        
        <div className="prose prose-invert prose-slate max-w-none space-y-6 text-slate-300">
          <section>
            <h2 className="text-xl font-bold text-white mb-4">Impressum und rechtliche Hinweise</h2>
            
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Erstinformation:</h3>
            <p>
              Ihr Vermittler verfügt über eine Gewerbeerlaubnis nach § 34d Abs.1 GewO als Versicherungsmakler 
              und ist unter der oben genannten Registernummer in das Vermittlerregister nach § 11a GewO eingetragen. 
              Er ist als Vermittler Ihr Ansprechpartner in den vereinbarten Versicherungsangelegenheiten und 
              persönlich verantwortlich für seine Beratung nach §§ 60, 61 und 63 VVG.
            </p>
          </section>
          
          <section>
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Statusinformationen gemäß § 11 Versicherungsvermittlungsverordnung</h3>
            <p>
              Christine Luise Staubach ist seit 01.08.2018 bei der IHK als Versicherungsmakler mit einer 
              Erlaubnis nach 34d Abs. 1 der Gewerbeordnung im Vermittlerregister eingetragen.
            </p>
          </section>
          
          <section>
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Zuständige Erlaubnisbehörde:</h3>
            <p>
              Industrie- und Handelskammer Bonn/Rhein-Sieg<br />
              Bonner Talweg 17<br />
              53113 Bonn<br />
              Tel. 0228-2284-0<br />
              Fax: 0228-2284-170
            </p>
          </section>
          
          <section>
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Die Eintragung im Vermittlerregister kann wie folgt überprüft werden:</h3>
            <p>
              Deutscher Industrie- und Handelskammertag (DIHK) e.V.<br />
              Breite Strasse 29<br />
              10178 Berlin<br />
              Telefon: 0180-500-585-0<br />
              <a href="https://www.vermittlerregister.org" className="text-lime-200 hover:underline" target="_blank" rel="noopener noreferrer">
                www.vermittlerregister.org
              </a>
            </p>
          </section>
          
          <section>
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Schlichtungsstellen:</h3>
            <p>
              Versicherungsombudsmann e.V.<br />
              Postfach 08 06 32<br />
              10006 Berlin<br />
              <a href="https://www.versicherungsombudsmann.de" className="text-lime-200 hover:underline" target="_blank" rel="noopener noreferrer">
                www.versicherungsombudsmann.de
              </a>
            </p>
            <p className="mt-4">
              Ombudsmann Private Kranken- und Pflegeversicherung<br />
              Postfach 06022<br />
              10052 Berlin<br />
              <a href="https://www.pkv-ombudsmann.de" className="text-lime-200 hover:underline" target="_blank" rel="noopener noreferrer">
                www.pkv-ombudsmann.de
              </a>
            </p>
          </section>
          
          <section>
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Hinweis gemäß Online-Streitbeilegungs-Verordnung</h3>
            <p>
              Nach geltendem Recht sind wir verpflichtet, Verbraucher auf die Existenz der Europäischen 
              Online-Streitbeilegungs-Plattform hinzuweisen, die für die Beilegung von Streitigkeiten 
              genutzt werden kann, ohne dass ein Gericht eingeschaltet werden muss.<br />
              <a href="http://ec.europa.eu/odr" className="text-lime-200 hover:underline" target="_blank" rel="noopener noreferrer">
                http://ec.europa.eu/odr
              </a>
            </p>
          </section>
          
          <section>
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Verantwortlich für den Inhalt dieser Webseite</h3>
            <p>
              <strong>Verantwortlich i.S.d. § 5TMG / § 55 RStV:</strong><br />
              Vers, Christine Luise Staubach<br />
              Harbigstr. 6<br />
              52249 Eschweiler
            </p>
            <p className="mt-4">
              <strong>Postanschrift:</strong><br />
              Christine Luise Staubach<br />
              Harbigstr. 6<br />
              52249 Eschweiler<br /><br />
              Telefon: 0177 / 715 2944<br />
              Telefax: 02 21 / 61 45 38<br />
              E-Mail: tina@pet-secure-club.de
            </p>
            <p className="mt-4">
              Geschäftsführer: Christine Luise Staubach<br />
              Hauptsitz: Eschweiler, Deutschland<br />
              Registrierungsnummer: D-L4E3-ZO9Q6-47
            </p>
          </section>
          
          <section>
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Ihr Vertragspartner</h3>
            <p>
              Ihr Vertragspartner ist stets die nachgenannte Firma und verfügt über eine Gewerbeerlaubnis 
              nach § 34d Abs.1 GewO als Versicherungsmakler.<br /><br />
              Christine Luise Staubach<br />
              Harbigstr. 6<br />
              52249 Eschweiler<br />
              tina@pet-secure-club.de<br />
              Telefon: 0177 / 715 2944
            </p>
            <p className="mt-4">
              <em>Es besteht eine gesetzeskonforme Vermögensschadenhaftpflicht, diese wurde der IHK nachgewiesen.</em>
            </p>
          </section>
          
          <section>
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Haftung für Links</h3>
            <p>
              Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen 
              Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. 
              Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der 
              Seiten verantwortlich.
            </p>
          </section>
          
          <section>
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Urheberrecht</h3>
            <p>
              Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen 
              dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art 
              der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen 
              Zustimmung des jeweiligen Autors bzw. Erstellers.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// Datenschutz Page Component
const DatenschutzPage = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-slate-900 py-20" data-testid="datenschutz-page">
      <div className="container-main px-4 max-w-3xl mx-auto">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-lime-200 mb-8 hover:underline"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Zurück zur Startseite
        </button>
        
        <h1 className="font-display text-4xl font-bold text-white mb-8">Datenschutzerklärung</h1>
        
        <div className="prose prose-invert prose-slate max-w-none space-y-6 text-slate-300">
          <section>
            <h2 className="text-xl font-bold text-white mb-4">1. Datenschutz auf einen Blick</h2>
            
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Allgemeine Hinweise</h3>
            <p>
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen 
              Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen 
              Sie persönlich identifiziert werden können.
            </p>
            
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Datenerfassung auf dieser Website</h3>
            <p>
              <strong>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong><br />
              Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. 
              Dessen Kontaktdaten können Sie dem Impressum dieser Website entnehmen.
            </p>
            <p className="mt-4">
              <strong>Wie erfassen wir Ihre Daten?</strong><br />
              Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. 
              Hierbei kann es sich z. B. um Daten handeln, die Sie in ein Kontaktformular eingeben.
            </p>
            <p className="mt-4">
              <strong>Wofür nutzen wir Ihre Daten?</strong><br />
              Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten.
            </p>
            <p className="mt-4">
              <strong>Welche Rechte haben Sie bezüglich Ihrer Daten?</strong><br />
              Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck 
              Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, 
              die Berichtigung oder Löschung dieser Daten zu verlangen.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4">2. Allgemeine Hinweise und Pflichtinformationen</h2>
            
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Datenschutz</h3>
            <p>
              Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. 
              Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend der gesetzlichen 
              Datenschutzvorschriften sowie dieser Datenschutzerklärung.
            </p>
            
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Hinweis zur verantwortlichen Stelle</h3>
            <p>
              Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:<br /><br />
              Christine Luise Staubach<br />
              Harbigstr. 6<br />
              52249 Eschweiler<br />
              Telefon: 0177 / 715 2944<br />
              E-Mail: tina@pet-secure-club.de
            </p>
            
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Widerruf Ihrer Einwilligung zur Datenverarbeitung</h3>
            <p>
              Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich. 
              Sie können eine bereits erteilte Einwilligung jederzeit widerrufen. Dazu reicht eine 
              formlose Mitteilung per E-Mail an uns.
            </p>
            
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Beschwerderecht bei der zuständigen Aufsichtsbehörde</h3>
            <p>
              Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein Beschwerderecht bei 
              einer Aufsichtsbehörde zu, insbesondere in dem Mitgliedstaat ihres gewöhnlichen Aufenthalts.
            </p>
            
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">SSL- bzw. TLS-Verschlüsselung</h3>
            <p>
              Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher 
              Inhalte eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie 
              daran, dass die Adresszeile des Browsers von „http://" auf „https://" wechselt und an 
              dem Schloss-Symbol in Ihrer Browserzeile.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4">3. Datenerfassung auf dieser Website</h2>
            
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Cookies</h3>
            <p>
              Unsere Internetseiten verwenden so genannte „Cookies". Cookies sind kleine Textdateien 
              und richten auf Ihrem Endgerät keinen Schaden an. Sie werden entweder vorübergehend für 
              die Dauer einer Sitzung (Session-Cookies) oder dauerhaft (permanente Cookies) auf Ihrem 
              Endgerät gespeichert.
            </p>
            <p className="mt-4">
              Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert 
              werden und Cookies nur im Einzelfall erlauben.
            </p>
            
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">Kontaktformular</h3>
            <p>
              Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem 
              Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung 
              der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben 
              wir nicht ohne Ihre Einwilligung weiter.
            </p>
          </section>
          
          <p className="text-slate-500 text-sm mt-8">
            Quelle: eRecht24
          </p>
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = ({ onBack }) => {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [period, setPeriod] = useState('today');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const login = () => {
    axios.post(`${API}/analytics/login?password=${password}`)
      .then(() => { setAuthenticated(true); })
      .catch(() => toast.error('Falsches Passwort'));
  };

  useEffect(() => {
    if (!authenticated) return;
    setLoading(true);
    axios.get(`${API}/analytics/dashboard?password=${password}&period=${period}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, [authenticated, period, password]);

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" data-testid="admin-login">
        <div className="glass-card rounded-2xl p-8 w-full max-w-sm">
          <h2 className="font-display text-2xl font-bold text-white mb-6 text-center">Admin Login</h2>
          <Input type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && login()}
            className="bg-slate-800/50 border-slate-700 text-white mb-4" placeholder="Passwort" data-testid="admin-password" />
          <Button onClick={login} className="w-full bg-lime-200 hover:bg-lime-300 text-slate-900 font-bold" data-testid="admin-login-btn">Einloggen</Button>
          <button onClick={onBack} className="w-full mt-3 text-slate-500 text-sm hover:text-white transition-colors">Zurück zur Seite</button>
        </div>
      </div>
    );
  }

  const funnel = data?.funnel || {};
  const maxFunnel = Math.max(data?.checkout_started || 1, 1);

  return (
    <div className="min-h-screen bg-slate-900 py-12" data-testid="admin-dashboard">
      <div className="container-main px-4 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-white">KICKBACK Analytics</h1>
            <p className="text-slate-500 text-sm mt-1">Conversion Funnel & Pageviews</p>
          </div>
          <button onClick={onBack} className="text-slate-400 hover:text-white text-sm transition-colors">Zur Seite</button>
        </div>

        {/* Period Selector */}
        <div className="flex gap-2 mb-8">
          {[
            { id: 'today', label: 'Heute' },
            { id: 'yesterday', label: 'Gestern' },
            { id: '7days', label: '7 Tage' },
          ].map(p => (
            <button key={p.id} onClick={() => setPeriod(p.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${period === p.id ? 'bg-lime-200 text-slate-900' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
              data-testid={`period-${p.id}`}
            >{p.label}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-lime-200 border-t-transparent rounded-full animate-spin" /></div>
        ) : data ? (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Pageviews', value: data.pageviews, color: 'text-white' },
                { label: 'Anträge gestartet', value: data.checkout_started, color: 'text-lime-200' },
                { label: 'Abgeschlossen', value: data.completed, color: 'text-emerald-400' },
                { label: 'Conversion', value: data.pageviews > 0 ? `${((data.completed / data.pageviews) * 100).toFixed(1)}%` : '0%', color: 'text-amber-400' },
              ].map((kpi, i) => (
                <div key={i} className="glass-card rounded-xl p-5">
                  <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">{kpi.label}</div>
                  <div className={`font-display text-3xl font-bold ${kpi.color}`}>{kpi.value}</div>
                </div>
              ))}
            </div>

            {/* Funnel */}
            <div className="glass-card rounded-2xl p-6 mb-8">
              <h3 className="text-white font-bold mb-6">Checkout Funnel</h3>
              <div className="space-y-3">
                {[
                  { step: 'Antrag gestartet', count: data.checkout_started, color: 'bg-lime-200' },
                  { step: 'Step 2 – Geburtsdatum', count: funnel.step_2 || 0, color: 'bg-lime-300' },
                  { step: 'Step 3 – Paketauswahl', count: funnel.step_3 || 0, color: 'bg-lime-400' },
                  { step: 'Step 4 – Risikocheck', count: funnel.step_4 || 0, color: 'bg-emerald-400' },
                  { step: 'Step 5 – Addon', count: funnel.step_5 || 0, color: 'bg-emerald-500' },
                  { step: 'Step 6 – Daten & IBAN', count: funnel.step_6 || 0, color: 'bg-emerald-600' },
                  { step: 'Abgeschlossen', count: data.completed, color: 'bg-emerald-400' },
                ].map((item, i) => {
                  const pct = maxFunnel > 0 ? (item.count / maxFunnel) * 100 : 0;
                  const prev = i === 0 ? null : [data.checkout_started, funnel.step_2, funnel.step_3, funnel.step_4, funnel.step_5, funnel.step_6, data.completed][i - 1] || 0;
                  const dropoff = prev !== null && prev > 0 ? `-${(((prev - item.count) / prev) * 100).toFixed(0)}%` : '';
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-400">{item.step}</span>
                        <div className="flex items-center gap-3">
                          {dropoff && i > 0 && <span className="text-red-400 text-xs">{dropoff}</span>}
                          <span className="text-white font-mono font-bold">{item.count}</span>
                        </div>
                      </div>
                      <div className="h-6 bg-slate-800 rounded-full overflow-hidden">
                        <div className={`h-full ${item.color} rounded-full transition-all duration-500`} style={{ width: `${Math.max(pct, 2)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent Applications */}
            {data.applications?.length > 0 && (
              <div className="glass-card rounded-2xl p-6">
                <h3 className="text-white font-bold mb-4">Letzte Anträge</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-500 text-xs uppercase tracking-wider border-b border-slate-700">
                        <th className="text-left py-3 px-2">Name</th>
                        <th className="text-left py-3 px-2">E-Mail</th>
                        <th className="text-left py-3 px-2">Paket</th>
                        <th className="text-left py-3 px-2">Preis</th>
                        <th className="text-left py-3 px-2">Addon</th>
                        <th className="text-left py-3 px-2">Datum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.applications.map((app, i) => (
                        <tr key={i} className="border-b border-slate-800 hover:bg-slate-800/50">
                          <td className="py-3 px-2 text-white">{app.firstName} {app.lastName}</td>
                          <td className="py-3 px-2 text-slate-400">{app.email}</td>
                          <td className="py-3 px-2"><span className="px-2 py-0.5 bg-lime-200/10 text-lime-200 rounded-full text-xs">{app.packageName}</span></td>
                          <td className="py-3 px-2 text-white font-mono">{app.monthlyPrice?.toFixed(2)}€</td>
                          <td className="py-3 px-2">{app.addon ? <span className="text-emerald-400">Ja</span> : <span className="text-slate-600">Nein</span>}</td>
                          <td className="py-3 px-2 text-slate-500 text-xs">{app.created_at?.slice(0, 16).replace('T', ' ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

// Countdown Timer - expires at midnight
const useCountdown = () => {
  const [timeLeft, setTimeLeft] = useState('');
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const end = new Date(now);
      end.setHours(23, 59, 59, 0);
      const diff = end - now;
      if (diff <= 0) { setTimeLeft('00:00:00'); return; }
      const h = String(Math.floor(diff / 3600000)).padStart(2, '0');
      const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, '0');
      const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, '0');
      setTimeLeft(`${h}:${m}:${s}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return timeLeft;
};

// Countdown Badge Component
const CountdownBadge = () => {
  const timeLeft = useCountdown();
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-black/20 rounded font-mono font-bold tracking-wider text-[11px]">
      {timeLeft}
    </span>
  );
};

// Cursor Glow Effect
const CursorGlow = () => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { damping: 30, stiffness: 200 });
  const smoothY = useSpring(mouseY, { damping: 30, stiffness: 200 });

  useEffect(() => {
    const handleMove = (e) => { mouseX.set(e.clientX); mouseY.set(e.clientY); };
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, [mouseX, mouseY]);

  return (
    <motion.div
      className="fixed pointer-events-none z-30 hidden md:block"
      style={{ x: smoothX, y: smoothY, translateX: '-50%', translateY: '-50%' }}
    >
      <div className="w-[500px] h-[500px] rounded-full bg-lime-200/[0.04] blur-[120px]" />
    </motion.div>
  );
};

// Main App Component
// Analytics tracking helper
const trackEvent = (event, data = {}) => {
  const sessionId = sessionStorage.getItem('kb_sid') || (() => { const id = Math.random().toString(36).slice(2); sessionStorage.setItem('kb_sid', id); return id; })();
  axios.post(`${API}/analytics/track`, { event, sessionId, ...data }).catch(() => {});
};

function App() {
  const [selectedPackage, setSelectedPackage] = useState('stammplatz');
  const [showCheckout, setShowCheckout] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(true);
  const [currentPage, setCurrentPage] = useState('home');
  const packageRef = useRef(null);
  const checkoutRef = useRef(null);

  // Track pageview
  useEffect(() => { trackEvent('pageview'); }, []);
  
  // Check if cookies already accepted
  useEffect(() => {
    const cookiesAccepted = localStorage.getItem('kickback_cookies_accepted');
    if (cookiesAccepted) {
      setShowCookieBanner(false);
    }
  }, []);
  
  useEffect(() => {
    const handleScroll = () => {
      // Show sticky CTA only when scrolled past hero and before checkout
      const shouldShow = window.scrollY > 600 && window.scrollY < 4000 && !showCheckout;
      setShowStickyCTA(shouldShow);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showCheckout]);
  
  const handleAcceptCookies = () => {
    localStorage.setItem('kickback_cookies_accepted', 'true');
    setShowCookieBanner(false);
  };
  
  const scrollToPackages = () => {
    handleSelectPackage('stammplatz');
  };
  
  const handleSelectPackage = (pkgId) => {
    setSelectedPackage(pkgId);
    setShowCheckout(true);
    trackEvent('checkout_start', { step: 1, packageId: pkgId });
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'ViewContent', { content_name: pkgId, currency: 'EUR' });
    }
  };
  
  const handleCheckoutComplete = (data) => {
    if (!data) { setShowCheckout(false); return; }
    setSuccessData(data);
    setShowSuccess(true);
    setShowCheckout(false);
  };
  
  const handleSuccessClose = () => {
    setShowSuccess(false);
    setShowCheckout(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleBackToHome = () => {
    setCurrentPage('home');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Check URL for admin page
  useEffect(() => {
    if (window.location.hash === '#admin') setCurrentPage('admin');
  }, []);

  // Render legal pages
  if (currentPage === 'admin') {
    return (
      <div className="min-h-screen bg-slate-900">
        <Toaster position="top-center" richColors />
        <AdminDashboard onBack={handleBackToHome} />
      </div>
    );
  }

  if (currentPage === 'impressum') {
    return (
      <div className="min-h-screen bg-slate-900">
        <ImpressumPage onBack={handleBackToHome} />
      </div>
    );
  }
  
  if (currentPage === 'datenschutz') {
    return (
      <div className="min-h-screen bg-slate-900">
        <DatenschutzPage onBack={handleBackToHome} />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-900" data-testid="app-container">
      <Toaster position="top-center" richColors />

      {/* Urgency Banner - Sticky on LP */}
      <div className="sticky top-0 z-50 bg-gradient-to-r from-[#FFD700] to-[#FF8C00] text-black text-center py-2.5 px-4 cursor-pointer hover:brightness-110 transition-all" onClick={scrollToPackages}>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium flex-wrap justify-center"><Zap className="w-3.5 h-3.5" /> 50% auf alle Tarife – ab <strong>4,99€</strong> statt <span className="line-through opacity-70">9,98€</span> · Endet in <CountdownBadge /> · <span className="underline font-bold">Jetzt sichern</span></span>
      </div>

      <ScrollProgress />
      <CursorGlow />
      <FloatingFootballs />
      
      <HeroSection onCtaClick={scrollToPackages} />
      <FieldDivider />
      <InjuryStat />
      <SpeedComparison />
      <FieldDivider />
      <InjuryCalculator />
      <FieldDivider />
      <ComparisonTable onCta={scrollToPackages} />
      <FieldDivider />
      <TestimonialsSection />
      <FieldDivider />
      
      <div ref={packageRef}>
        <PackageSelection 
          onSelectPackage={handleSelectPackage} 
          selectedPackage={selectedPackage}
        />
      </div>
      
      <FieldDivider />
      <FAQSection />
      <FieldDivider />
      <TeamSection />
      <FieldDivider />
      <PressSection onCta={scrollToPackages} />
      
      <TrustFooter setCurrentPage={setCurrentPage} />
      
      <StickyCTA visible={showStickyCTA} onClick={scrollToPackages} />
      
      <AnimatePresence>
        {showCheckout && (
          <CheckoutForm 
            selectedPackage={selectedPackage} 
            onComplete={handleCheckoutComplete}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSuccess && (
          <SuccessAnimation onClose={handleSuccessClose} userData={successData} />
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {showCookieBanner && (
          <CookieBanner onAccept={handleAcceptCookies} />
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
